<template>
  <div :style="{ direction: adminLang === 'he' ? 'rtl' : 'ltr', fontFamily: 'system-ui', background: '#f0f4f8', minHeight: '100vh' }">

    <!-- ═══ EMAIL NOT VERIFIED ════════════════════════════════════ -->
    <div v-if="emailNotVerified" class="auth-screen">
      <div class="auth-card">
        <div class="auth-logo">📧</div>
        <h2>{{ t('emailNotVerified') }}</h2>
        <p class="text-muted">{{ t('emailNotVerifiedMsg') }}</p>
        <button class="btn-google mt-3" @click="goToDashboard">{{ t('backToDashboard') }}</button>
      </div>
    </div>

    <!-- ═══ NOT AUTHENTICATED ════════════════════════════════════ -->
    <div v-else-if="!authenticated" class="auth-screen">
      <div class="auth-card">
        <div class="auth-logo">🗺️</div>
        <h2>{{ t('editorTitle') }}</h2>
        <p class="text-muted">{{ t('loginPrompt') }}</p>
        <div v-if="authError" class="error-msg">{{ t('loginError') }}</div>
        <button class="btn-google" :disabled="authLoading" @click="signInWithGoogle">
          <span v-if="authLoading">{{ t('connecting') }}</span>
          <span v-else>
            <svg width="18" height="18" viewBox="0 0 48 48" style="vertical-align:middle;margin-right:8px"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
            {{ t('signInGoogle') }}
          </span>
        </button>
      </div>
    </div>

    <!-- ═══ GAME SELECTION (si pas de ?game= dans l'URL) ═════════ -->
    <div v-else-if="!navigationName" class="auth-screen">
      <div class="auth-card" style="max-width: 480px">
        <div class="auth-logo">🎮</div>
        <h2>{{ t('myGames') }}</h2>
        <div class="account-row">
          <span class="account-email">{{ currentUser.email }}</span>
          <button class="btn-account-logout" @click="logout">{{ t('logout') }}</button>
        </div>
        <div v-if="loading" class="spinner-wrap"><div class="spinner"></div></div>
        <div v-else class="game-list">
          <button v-for="name in myGames" :key="name" class="game-item" @click="startEditing(name)">
            {{ name }}
          </button>
          <div v-if="myGames.length === 0" class="text-muted" style="padding:20px">
            {{ t('noGamesFound') }}
          </div>
        </div>
        <button class="btn-secondary mt-3" @click="goToDashboard">{{ t('backToDashboard') }}</button>
      </div>
    </div>

    <!-- ═══ MAIN EDITOR ══════════════════════════════════════════ -->
    <div v-else>

      <!-- Delete confirmation dialog -->
      <div v-if="deleteConfirm.visible" class="delete-overlay" @click.self="deleteConfirm.visible = false">
        <div class="delete-dialog">
          <div class="delete-dialog-icon">🗑️</div>
          <div class="delete-dialog-title">{{ t('deleteTitle') }} {{ deleteConfirm.pointNumber }} ?</div>
          <div class="delete-dialog-sub">{{ t('deleteMsg') }}</div>
          <div class="delete-dialog-actions">
            <button class="delete-btn-cancel" @click="deleteConfirm.visible = false">{{ t('cancel') }}</button>
            <button class="delete-btn-confirm" :disabled="deleteConfirm.loading" @click="confirmDeletePoint">
              <span v-if="deleteConfirm.loading">{{ t('deleting') }}</span>
              <span v-else>{{ t('confirmDelete') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Top bar -->
      <div class="topbar">
        <div class="topbar-row topbar-row--title">
          <div class="topbar-game">
            <span class="topbar-game-label">{{ t('gameLabel') }}</span>
            <span class="topbar-game-name">🗺️ {{ navigationName }}</span>
          </div>
          <button class="topbar-logout" @click="logout">{{ t('logout') }}</button>
        </div>
        <div class="topbar-row topbar-row--tabs">
          <button :class="['tab-btn', { active: activeTab === 'points' }]" @click="activeTab = 'points'; loadPoints()">
            {{ t('tabPoints') }} ({{ allPoints.length }})
          </button>
          <button :class="['tab-btn', { active: activeTab === 'settings' }]" @click="activeTab = 'settings'; loadSettings()">
            {{ t('tabSettings') }}
          </button>
        </div>
        <div class="topbar-row topbar-row--actions">
          <div v-if="availableLangs.length > 1" class="lang-switcher">
            <button
              v-for="l in availableLangs" :key="l"
              :class="['lang-btn', { active: currentLang === l }]"
              @click="switchLang(l)"
            >{{ l.toUpperCase() }}</button>
          </div>
          <div class="admin-lang-switcher">
            <button :class="['admin-lang-btn', adminLang==='fr' ? 'admin-lang-active' : '']" @click="adminLang='fr'">🇫🇷</button>
            <button :class="['admin-lang-btn', adminLang==='en' ? 'admin-lang-active' : '']" @click="adminLang='en'">🇬🇧</button>
            <button :class="['admin-lang-btn', adminLang==='he' ? 'admin-lang-active' : '']" @click="adminLang='he'">🇮🇱</button>
          </div>
          <button class="topbar-back" @click="goToDashboard">{{ t('backToDashboard') }}</button>
        </div>
      </div>

      <!-- ─────────────────── TAB: POINTS ─────────────────── -->
      <div v-show="activeTab === 'points'" class="tab-content">
        <div v-if="loading" class="spinner-wrap"><div class="spinner"></div></div>
        <div v-else>

          <template v-if="!onboarding">
          <div v-for="point in allPoints" :key="point.pointNumber" :id="'point-card-' + point.pointNumber" class="card" :class="{ 'card--editing': point.editMode }">
            <div class="card-header" @click="togglePoint(point)">
              <div class="card-title">
                <span class="point-badge">{{ point.pointNumber }}</span>
                <div class="point-meta">
                  <div class="point-meta-row">
                    <span class="point-phase-label">📱</span>
                    <span class="point-screen-badge">{{ screenName(point.navigationScreen || 'imagesList') }}</span>
                    <span v-if="point.navigationScreen === 'missingWord' && point.missingWord" class="point-missing-word">{{ point.missingWord }}</span>
                  </div>
                  <div class="point-meta-row">
                    <span class="point-phase-label">ℹ️</span>
                    <span v-if="point.pointInformation" class="point-info-badge text">{{ t('labelText') }}</span>
                    <span v-if="point.videoUrl" class="point-info-badge video">{{ t('labelVideo') }}</span>
                    <span v-if="point.audioUrl" class="point-info-badge audio">{{ t('labelAudio') }}</span>
                    <span v-if="point.withAR" class="point-info-badge ar">{{ t('labelAR') }}</span>
                    <span v-if="point.withPhone" class="point-info-badge phone">{{ t('labelPhone') }}</span>
                    <span v-if="!point.pointInformation && !point.videoUrl && !point.audioUrl && !point.withAR && !point.withPhone" class="point-info-badge empty">—</span>
                  </div>
                  <div class="point-meta-row">
                    <span class="point-phase-label">🎯</span>
                    <template v-if="point.navigationOnly">
                      <span class="point-navonly-badge">{{ t('navOnly') }}</span>
                    </template>
                    <template v-else>
                      <span class="point-picked-badge">{{ pickedName(point.picked || 'questions') }}</span>
                      <span class="point-q-badge">{{ questionsForPoint(point.pointNumber).length }} Q</span>
                    </template>
                  </div>
                </div>
              </div>
              <div class="card-actions">
                <span v-if="!point.editMode" class="chevron">{{ point._open ? '▲' : '▼' }}</span>
                <button v-if="point.editMode" class="btn-save" :disabled="!pointDirty(point)" @click.stop="savePoint(point)">{{ t('save') }}</button>
                <button v-if="point.editMode" class="btn-cancel" @click.stop="cancelEdit(point)">✕</button>
                <button v-if="!point.editMode && point._open" class="btn-edit" @click.stop="point.editMode = true">{{ t('edit') }}</button>
                <button v-if="!point.editMode && point._open" class="btn-delete-point" @click.stop="askDeletePoint(point)">🗑️</button>
              </div>
            </div>

            <!-- READ VIEW -->
            <div v-if="point._open && !point.editMode" class="sections-wrapper">
              <!-- ── Phase 1 : Écran de navigation ── -->
              <div class="section-card">
                <div class="sc-header">
                  <span class="sc-title sc-title--screen">{{ t('sectionScreen') }}</span>
                  <span class="help-tip">?<span class="help-tip-text">{{ t('sectionScreenHint') }}</span></span>
                </div>
                <span class="point-screen-badge">{{ screenName(point.navigationScreen || 'imagesList') }}</span>
                <img v-if="point.imageUrl" :src="point.imageUrl" class="point-thumb mt-2" alt="" />
                <div v-if="point.navigationScreen === 'missingWord'" class="read-field mt-2">
                  <span class="label">{{ t('missingWordReadLabel') }}</span>
                  <span class="read-value">{{ point.missingWord || '—' }}</span>
                </div>
              </div>
              <!-- ── Phase 2 : Écran d'information ── -->
              <div class="section-card">
                <div class="sc-header">
                  <span class="sc-title sc-title--info">{{ t('sectionInfo') }}</span>
                  <span class="help-tip">?<span class="help-tip-text">{{ t('sectionInfoHint') }}</span></span>
                </div>
                <div v-if="point.pointInformation" class="read-field">
                  <span class="label">{{ t('labelText') }}</span>
                  <p class="info-text">{{ point.pointInformation }}</p>
                </div>
                <div v-if="point.audioUrl" class="read-field">
                  <span class="label">{{ t('labelAudio') }}</span>
                  <span class="read-value">{{ point.audioUrl }}</span>
                </div>
                <div v-if="point.videoUrl" class="read-field">
                  <span class="label">{{ t('labelVideoId') }}</span>
                  <span class="read-value">{{ point.videoUrl }}</span>
                </div>
                <div v-if="point.withAR" class="read-field">
                  <span class="label">{{ t('labelAR') }}</span>
                  <span class="read-value">🧭 {{ arDirectionLabel(point.arDirection) }} — {{ point.arVideoUrl ? '✅ GIF configuré' : '⚠️ GIF manquant' }}</span>
                </div>
                <div v-if="point.withPhone" class="read-field">
                  <span class="label">{{ t('labelPhone') }}</span>
                  <span class="read-value">{{ point.callerName || '—' }} · {{ point.phoneAudioUrl || t('noAudio') }}</span>
                </div>
                <div v-if="!point.pointInformation && !point.audioUrl && !point.videoUrl && !point.withAR && !point.withPhone" class="read-value muted">—</div>
              </div>
              <!-- ── Phase 3 : Écran d'exercice ── -->
              <div class="section-card">
                <div class="sc-header">
                  <span class="sc-title sc-title--exercise">{{ t('sectionExercise') }}</span>
                  <span class="help-tip">?<span class="help-tip-text">{{ t('sectionExerciseHint') }}</span></span>
                </div>
                <span class="point-picked-badge">{{ pickedName(point.picked || 'questions') }}</span>
                <div v-if="point.picked === 'puzzle'" class="mt-2">
                  <img v-if="point.puzzleImageBase64 || point.puzzleImageUrl" :src="point.puzzleImageBase64 || point.puzzleImageUrl" class="puzzle-thumb" alt="puzzle" />
                  <span v-else class="muted">{{ t('noPuzzleImage') }}</span>
                </div>
                <div v-if="point.picked === 'memory'" class="mt-2 muted">
                  {{ t('memoryUsesPointImages') }}
                </div>
                <div v-if="point.picked === 'multiplematch'" class="mm-read-grid mt-2">
                  <div v-for="n in 4" :key="n" class="mm-pair-view">
                    <img v-if="point['itemImage' + n]" :src="point['itemImage' + n]" class="mm-thumb" alt="" />
                    <div v-else class="mm-empty">—</div>
                    <span class="mm-desc">{{ point['description' + n] || '—' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- EDIT VIEW -->
            <div v-if="point._open && point.editMode" class="sections-wrapper">
              <!-- Numéro du point -->
              <div class="section-card section-card--compact">
                <div class="field-row" style="margin:0">
                  <div class="field-group">
                    <label class="field-label">{{ t('pointNumber') }}</label>
                    <input v-model.number="point.pointNumber" type="number" class="form-input-sm" />
                  </div>
                </div>
              </div>

              <!-- ── Phase 1 : Écran de navigation ── -->
              <div class="section-card">
              <div class="sc-header">
                <span class="sc-title sc-title--screen">{{ t('sectionScreen') }}</span>
                <span class="help-tip">?<span class="help-tip-text">{{ t('sectionScreenHint') }}</span></span>
              </div>
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('navigationScreen') }}</label>
                  <select v-model="point.navigationScreen" class="form-input-sm">
                    <option v-if="hasFeature('imageList')  || !point.navigationScreen || point.navigationScreen === 'imagesList'" value="imagesList">{{ t('screenImagesList') }}</option>
                    <option v-if="hasFeature('dynamicMap') || point.navigationScreen === 'dynamicMap'"  value="dynamicMap">{{ t('screenDynamicMap') }}</option>
                    <option v-if="hasFeature('carousel')   || point.navigationScreen === 'carousel'"    value="carousel">{{ t('screenCarousel') }}</option>
                    <option v-if="hasFeature('missingWord')|| point.navigationScreen === 'missingWord'" value="missingWord">{{ t('screenMissingWord') }}</option>
                  </select>
                </div>
              </div>
              <div v-if="point.navigationScreen === 'missingWord'" class="field-group">
                <label class="field-label">{{ t('missingWordLabel') }}</label>
                <input v-model="point.missingWord" class="form-input-sm" />
              </div>
              <div class="field-group mt-2">
                <label class="field-label">{{ t('image') }}</label>
                <div v-if="point.imagePreview || point.imageUrl" class="thumb-row">
                  <img :src="point.imagePreview || point.imageUrl" class="point-thumb mt-2 mb-2" alt="" />
                  <button class="btn-edit-photo" @click="openPhotoEditor(point)">{{ t('editPhoto') }}</button>
                </div>
                <label class="file-btn">
                  {{ point.imageUrl ? t('changeImage') : t('chooseImage') }}
                  <input type="file" accept="image/*" style="display:none" @change="(e) => onImagePicked(e, point)" />
                </label>
              </div>

              </div><!-- /section-card screen -->

              <!-- ── Phase 2 : Écran d'information ── -->
              <div class="section-card">
              <div class="sc-header">
                <span class="sc-title sc-title--info">{{ t('sectionInfo') }}</span>
                <span class="help-tip">?<span class="help-tip-text">{{ t('sectionInfoHint') }}</span></span>
              </div>
              <div class="field-row checkbox-row">
                <label v-if="hasFeature('texte')  || !!point.pointInformation" class="check-label">
                  <input type="checkbox"
                    :checked="point._showText !== undefined ? point._showText : !!point.pointInformation"
                    @change="e => { point._showText = e.target.checked; $forceUpdate(); }" /> {{ t('labelText') }}
                </label>
                <label v-if="hasFeature('audio')  || !!point.audioUrl" class="check-label">
                  <input type="checkbox"
                    :checked="point._showAudio !== undefined ? point._showAudio : !!point.audioUrl"
                    @change="e => { point._showAudio = e.target.checked; $forceUpdate(); }" /> {{ t('labelAudio') }}
                </label>
                <label v-if="hasFeature('video')  || !!point.videoUrl" class="check-label">
                  <input type="checkbox" v-model="point.withVideo"
                    @change="e => { if (!e.target.checked) { point.videoUrl = ''; $forceUpdate(); } }" /> {{ t('labelVideo') }}
                </label>
                <label v-if="hasFeature('ar')     || !!point.withAR" class="check-label">
                  <input type="checkbox" v-model="point.withAR" /> {{ t('labelAR') }}
                </label>
                <label v-if="hasFeature('phone')  || !!point.withPhone" class="check-label">
                  <input type="checkbox"
                    :checked="point._showPhone !== undefined ? point._showPhone : !!point.withPhone"
                    @change="e => { point._showPhone = e.target.checked; point.withPhone = e.target.checked; if (!e.target.checked) { point.phoneAudioUrl = ''; point.callerName = ''; } $forceUpdate(); }" /> {{ t('labelPhone') }}
                </label>
              </div>
              <div v-if="point._showPhone !== undefined ? point._showPhone : !!point.withPhone" class="field-group">
                <label class="field-label">{{ t('callerName') }}</label>
                <input v-model="point.callerName" class="form-input-sm" :placeholder="t('callerNamePlaceholder')" />
                <label class="field-label mt-1">{{ t('phoneAudioUrl') }}</label>
                <input v-model="point.phoneAudioUrl" class="form-input-sm" placeholder="https://…" />
              </div>
              <div v-if="point._showText !== undefined ? point._showText : !!point.pointInformation" class="field-group">
                <label class="field-label">{{ t('labelText') }}</label>
                <textarea v-model="point.pointInformation" class="form-textarea" rows="4"></textarea>
              </div>
              <div v-if="point._showAudio !== undefined ? point._showAudio : !!point.audioUrl" class="field-group">
                <label class="field-label">{{ t('audioUrl') }}</label>
                <input v-model="point.audioUrl" class="form-input-sm" placeholder="https://…" />
              </div>
              <div v-if="point.withVideo" class="field-group">
                <label class="field-label">{{ t('youtubeUrl') }} <span v-if="point.withVerticalVideo" style="color:#f59e0b;font-size:11px">{{ t('shortsDetected') }}</span></label>
                <input :value="point.videoUrl" class="form-input-sm" placeholder="https://youtube.com/shorts/... ou dQw4w9WgXcQ"
                  @input="e => parseVideoInput(e.target.value, point)" />
                <div v-if="point.withVideo && !point.videoUrl" class="field-warn">
                  {{ t('youtubeWarning') }}
                </div>
              </div>
              <div v-if="point.withAR" class="field-group">
                <label class="field-label mt-1">{{ t('arDirection') }}</label>
                <div class="ar-dir-toggle mt-1">
                  <button :class="['ar-dir-btn', !point.arDirectionExact ? 'active' : '']" @click="point.arDirectionExact = false">{{ t('arPreset') }}</button>
                  <button :class="['ar-dir-btn', point.arDirectionExact ? 'active' : '']" @click="point.arDirectionExact = true">{{ t('arExactDeg') }}</button>
                </div>
                <select v-if="!point.arDirectionExact" v-model.number="point.arDirection" class="form-input-sm mt-1">
                  <option :value="0">{{ t('arNorth') }}</option>
                  <option :value="45">{{ t('arNE') }}</option>
                  <option :value="90">{{ t('arEast') }}</option>
                  <option :value="135">{{ t('arSE') }}</option>
                  <option :value="180">{{ t('arSouth') }}</option>
                  <option :value="225">{{ t('arSW') }}</option>
                  <option :value="270">{{ t('arWest') }}</option>
                  <option :value="315">{{ t('arNW') }}</option>
                </select>
                <div v-else class="ar-deg-input mt-1">
                  <input v-model.number="point.arDirection" type="number" min="0" max="359" class="form-input-sm" placeholder="0–359" />
                  <span class="ar-deg-unit">°</span>
                </div>
                <label class="field-label mt-2">{{ t('arEffectUrl') }}</label>
                <input v-model="point.arVideoUrl" class="form-input-sm" placeholder="https://..." />
                <label class="field-label mt-2">{{ t('arSearchWord') }}</label>
                <input v-model="point.arSearchWord" class="form-input-sm" :placeholder="t('arSearchWordPlaceholder')" />
                <div class="field-hint">{{ t('arSearchWordHint') }}</div>
                <div class="ar-gif-options">
                  <div class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arGifCount') }}</label>
                    <select v-model.number="point.arGifCount" class="form-input-sm">
                      <option :value="1">{{ t('arGif1') }}</option>
                      <option :value="2">{{ t('arGif2') }}</option>
                      <option :value="3">3</option>
                    </select>
                  </div>
                  <div v-if="point.arGifCount > 1" class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arSpacing') }}</label>
                    <input v-model.number="point.arGifSpacing" type="number" class="form-input-sm" placeholder="0" />
                    <div class="field-hint">{{ t('arSpacingHint') }}</div>
                  </div>
                </div>
                <label class="field-label mt-2">{{ t('arGrowAnimation') }}</label>
                <div class="ar-anim-toggle">
                  <button :class="['ar-dir-btn', point.arGrowAnimation !== false ? 'active' : '']" @click="point.arGrowAnimation = true">{{ t('arGrow') }}</button>
                  <button :class="['ar-dir-btn', point.arGrowAnimation === false ? 'active' : '']" @click="point.arGrowAnimation = false">{{ t('arAppear') }}</button>
                </div>
              </div>

              </div><!-- /section-card info -->

              <!-- ── Phase 3 : Écran d'exercice ── -->
              <div class="section-card">
              <div class="sc-header">
                <span class="sc-title sc-title--exercise">{{ t('sectionExercise') }}</span>
                <span class="help-tip">?<span class="help-tip-text">{{ t('sectionExerciseHint') }}</span></span>
              </div>
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('exerciseType') }}</label>
                  <select v-model="point.picked" class="form-input-sm">
                    <option v-if="hasFeature('QCM')          || !point.picked || point.picked === 'questions'"    value="questions">{{ t('exerciseQuestions') }}</option>
                    <option v-if="hasFeature('puzzle')        || point.picked === 'puzzle'"                        value="puzzle">{{ t('exercisePuzzle') }}</option>
                    <option v-if="hasFeature('multipleMatch') || point.picked === 'multiplematch'"                 value="multiplematch">{{ t('exerciseMultipleMatch') }}</option>
                    <option v-if="hasFeature('memory')        || point.picked === 'memory'"                        value="memory">{{ t('exerciseMemory') }}</option>
                    <option v-if="hasFeature('trueFalse')     || point.picked === 'justefaux'"                     value="justefaux">{{ t('exerciseJusteFaux') }}</option>
                    <option v-if="hasFeature('motManquant')   || point.picked === 'motManquant'"                   value="motManquant">{{ t('exerciseMotManquant') }}</option>
                    <option v-if="hasFeature('rushHour')      || point.picked === 'rushhour'"                      value="rushhour">{{ t('exerciseRushHour') }}</option>
                    <option v-if="hasFeature('signcard')      || point.picked === 'signcard'"                      value="signcard">{{ t('exerciseSignCard') }}</option>
                  </select>
                </div>
              </div>
              <div v-if="point.picked === 'signcard'" class="field-group mt-2">
                <div class="section-divider">{{ t('signCardTitle') }}</div>
                <label class="field-label">{{ t('signCardImage') }}</label>
                <input type="file" accept="image/*" class="form-input-sm mt-1" @change="(e) => onSignCardImagePicked(e, point)" />
                <div v-if="point.signCardImageUrl || point.signCardImageBase64" class="sc-admin-preview-wrap mt-2">
                  <img :src="point.signCardImageUrl || point.signCardImageBase64" class="sc-admin-img" />
                  <div class="sc-admin-zone" :style="signCardZoneStyle(point)">{{ t('signCardZone') }}</div>
                </div>
                <label class="field-label mt-2">{{ t('signCardHeight') }} {{ point.signCardSigHeightPct || 20 }}%</label>
                <input type="range" min="5" max="50" step="5" class="sc-range mt-1"
                  :value="point.signCardSigHeightPct || 20"
                  @input="point.signCardSigHeightPct = +$event.target.value; $forceUpdate()" />
                <label class="field-label mt-2">{{ t('signCardVAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="v in [{k:'top',tKey:'alignTop'},{k:'center',tKey:'alignCenter'},{k:'bottom',tKey:'alignBottom'}]" :key="v.k"
                    :class="['sc-align-btn', (point.signCardSigVAlign || 'bottom') === v.k ? 'sc-align-active' : '']"
                    @click="point.signCardSigVAlign = v.k; $forceUpdate()">{{ t(v.tKey) }}</button>
                </div>
                <label class="field-label mt-2">{{ t('signCardHAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="h in [{k:'left',tKey:'alignLeft'},{k:'center',tKey:'alignCenter'},{k:'right',tKey:'alignRight'}]" :key="h.k"
                    :class="['sc-align-btn', (point.signCardSigHAlign || 'center') === h.k ? 'sc-align-active' : '']"
                    @click="point.signCardSigHAlign = h.k; $forceUpdate()">{{ t(h.tKey) }}</button>
                </div>
              </div>
              <div v-if="point.picked === 'rushhour'" class="field-group mt-2">
                <div class="section-divider">{{ t('rushHourTitle') }}</div>
                <RushHourEditor v-model="point.rushHourVehicles" />
              </div>
              <div v-if="point.picked === 'puzzle'" class="field-group">
                <label class="field-label">{{ t('puzzleImage') }}</label>
                <div class="upload-hint">{{ t('puzzleImageHint') }}</div>
                <div v-if="point.puzzleImageBase64 || point.puzzleImageUrl" class="thumb-row mt-2 mb-1">
                  <img :src="point.puzzleImageBase64 || point.puzzleImageUrl" class="puzzle-thumb" alt="" />
                </div>
                <label class="file-btn mt-1">
                  {{ (point.puzzleImageBase64 || point.puzzleImageUrl) ? t('changeImage') : t('chooseImage') }}
                  <input type="file" accept="image/*" style="display:none" @change="(e) => onPuzzleImagePicked(e, point)" />
                </label>
              </div>
              <div v-if="point.picked === 'memory'" class="field-group mt-2">
                <div class="section-divider">{{ t('memoryImages') }}</div>
                <div class="upload-hint">{{ t('memoryImagesHint') }}</div>
                <label class="file-btn mt-1">
                  {{ t('addImage') }} ({{ (point.memoryImages || []).length }}/6)
                  <input v-if="(point.memoryImages || []).length < 6" type="file" accept="image/*" style="display:none" @change="e => onMemoryImagePicked(e, point)" />
                </label>
                <div class="mem-img-preview-row">
                  <div v-for="(img, i) in (point.memoryImages || [])" :key="i" class="mem-img-preview">
                    <img :src="img" alt="" />
                    <button class="mem-img-remove" @click="point.memoryImages.splice(i, 1)">✕</button>
                  </div>
                </div>
              </div>
              <div v-if="point.picked === 'multiplematch'">
                <div class="section-divider">{{ t('mmTitle') }}</div>
                <div class="upload-hint">{{ t('mmHint') }}</div>
                <div v-for="n in 4" :key="n" class="mm-pair-edit">
                  <div class="mm-pair-header">{{ t('mmPair') }} {{ n }}</div>
                  <div class="mm-pair-row">
                    <div class="mm-img-col">
                      <input type="file" accept="image/*" class="form-input-sm" @change="e => onMmImagePicked(e, point, n)" />
                      <img v-if="point['itemImage' + n]" :src="point['itemImage' + n]" class="mm-thumb mt-1" alt="" />
                      <button v-if="point['itemImage' + n]" class="btn-edit-photo mt-1" @click="openPhotoEditor(point, 'itemImage' + n)">{{ t('editPhoto') }}</button>
                    </div>
                    <div class="mm-label-col">
                      <label class="field-label">{{ t('mmLabel') }}</label>
                      <input v-model="point['description' + n]" class="form-input-sm" :placeholder="t('mmLabelPlaceholder') + ' ' + n" />
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="point.picked === 'motManquant'" class="field-group mt-2">
                <div class="section-divider">{{ t('exerciseMotManquant') }}</div>
                <div class="upload-hint">{{ t('mmqHint') }} <strong>{mot}</strong></div>
                <textarea v-model="point.motManquantText" class="form-textarea" rows="5"
                  :placeholder="t('mmqPlaceholder')" />
                <div v-if="point.motManquantText" class="field-hint">
                  {{ (point.motManquantText.match(/\{[^}]+\}/g) || []).length }} {{ t('mmqHoles') }} :
                  {{ (point.motManquantText.match(/\{([^}]+)\}/g) || []).map(m => m.slice(1,-1)).join(', ') }}
                </div>
                <label class="field-label mt-2">{{ t('mmqMode') }}</label>
                <div class="mm-mode-toggle">
                  <button :class="['mm-mode-btn', (point.motManquantMode || 'visible') === 'visible' ? 'mm-mode-active' : '']"
                    @click="point.motManquantMode = 'visible'; $forceUpdate()">{{ t('mmqVisible') }}</button>
                  <button :class="['mm-mode-btn', point.motManquantMode === 'hidden' ? 'mm-mode-active' : '']"
                    @click="point.motManquantMode = 'hidden'; $forceUpdate()">{{ t('mmqHidden') }}</button>
                </div>
              </div>
              <div v-if="point.picked === 'justefaux'" class="field-group mt-2">
                <div class="section-divider">{{ t('jfTitle') }}</div>
                <label class="field-label">{{ t('jfQuestion') }}</label>
                <input v-model="point.justeFauxQuestion" class="form-input-sm" :placeholder="t('jfQuestionPlaceholder')" />
                <label class="field-label mt-2">{{ t('jfOptions') }}</label>
                <div v-for="(item, idx) in normalizeJFItems(point.justeFauxItems)" :key="idx" class="jf-admin-row">
                  <input v-model="item.label" class="form-input-sm jf-label-input" :placeholder="t('jfOptionPlaceholder')" />
                  <button :class="['jf-toggle-btn', item.correct === true ? 'jf-active-vrai' : '']" @click="item.correct = true; $forceUpdate()">✓</button>
                  <button :class="['jf-toggle-btn', item.correct !== true ? 'jf-active-faux' : '']" @click="item.correct = false; $forceUpdate()">✗</button>
                  <button class="jf-remove-btn" @click="removeJusteFauxItem(point, idx)">🗑</button>
                </div>
                <button class="btn-ghost mt-2" @click="addJusteFauxItem(point)">{{ t('jfAddOption') }}</button>
              </div>
              </div><!-- /section-card exercise -->
            </div><!-- /sections-wrapper edit -->

            <!-- Questions du point -->
            <div v-if="point._open && (point.picked === 'questions' || !point.picked)" class="section-card section-card--questions">
              <div class="sc-header">
                <span class="sc-title sc-title--exercise">{{ t('questionsTitle') }} ({{ questionsForPoint(point.pointNumber).length }})</span>
              </div>
              <div v-for="q in questionsForPoint(point.pointNumber)" :key="q.key" class="pq-card">
                <div class="pq-card-header pq-card-header--read">
                  <span class="pq-text">{{ q.data.question }}</span>
                  <div v-if="point.editMode" style="display:flex;gap:4px;flex-shrink:0">
                    <button v-if="!q.editMode" class="btn-edit" @click.stop="q.editMode = true; $forceUpdate()">✏️</button>
                    <button v-if="q.editMode" class="btn-save" @click.stop="saveQuestion(q)">💾</button>
                    <button v-if="q.editMode" class="btn-cancel" @click.stop="q.editMode = false; $forceUpdate()">✕</button>
                  </div>
                </div>
                <div v-if="!q.editMode" class="pq-body">
                  <div class="answer correct">✅ {{ q.data.correctAnswer }}</div>
                  <div class="answer wrong">✗ {{ q.data.wrongAnswer1 }}</div>
                  <div class="answer wrong">✗ {{ q.data.wrongAnswer2 }}</div>
                  <div class="answer wrong">✗ {{ q.data.wrongAnswer3 }}</div>
                </div>
                <div v-if="q.editMode" class="pq-body edit-form">
                  <div class="field-group"><label class="field-label">{{ t('jfQuestion') }}</label>
                    <textarea v-model="q.data.question" class="form-textarea" rows="2"></textarea></div>
                  <div class="field-group"><label class="field-label">{{ t('correctAnswer') }}</label>
                    <input v-model="q.data.correctAnswer" class="form-input-sm correct-input" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer1') }}</label>
                    <input v-model="q.data.wrongAnswer1" class="form-input-sm" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer2') }}</label>
                    <input v-model="q.data.wrongAnswer2" class="form-input-sm" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer3') }}</label>
                    <input v-model="q.data.wrongAnswer3" class="form-input-sm" /></div>
                </div>
              </div>
              <!-- Ajout d'une question : uniquement en mode édition -->
              <template v-if="point.editMode">
                <div v-if="point._addingQuestion" class="pq-card pq-new">
                  <div class="field-group"><label class="field-label">{{ t('jfQuestion') }} *</label>
                    <textarea v-model="point._nq.question" class="form-textarea" rows="2"></textarea></div>
                  <div class="field-group"><label class="field-label">{{ t('correctAnswer') }} *</label>
                    <input v-model="point._nq.correctAnswer" class="form-input-sm correct-input" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer1') }}</label>
                    <input v-model="point._nq.wrongAnswer1" class="form-input-sm" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer2') }}</label>
                    <input v-model="point._nq.wrongAnswer2" class="form-input-sm" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer3') }}</label>
                    <input v-model="point._nq.wrongAnswer3" class="form-input-sm" /></div>
                  <div class="row-buttons mt-3">
                    <button class="btn-secondary" @click="point._addingQuestion = false; $forceUpdate()">{{ t('cancel') }}</button>
                    <button class="btn-success" @click="submitQuestionForPoint(point)">{{ t('save') }}</button>
                  </div>
                </div>
                <button v-else class="pqs-add-btn" @click="point._addingQuestion = true; $forceUpdate()">{{ t('addQuestion') }}</button>
              </template>
            </div>

            <!-- COORDONNÉES (vue lecture) -->
            <div v-if="point._open && !point.editMode" class="section-card section-card--standalone">
              <div class="sc-header">
                <span class="sc-title sc-title--position">{{ t('coordinates') }}</span>
                <span class="help-tip">?<span class="help-tip-text">{{ t('sectionPositionHint') }}</span></span>
              </div>
              <div class="read-grid">
                <div class="read-item"><span class="label">Lat</span><span>{{ point.lat || '—' }}</span></div>
                <div class="read-item"><span class="label">Lng</span><span>{{ point.lng || '—' }}</span></div>
              </div>
            </div>

            <!-- COORDONNÉES (vue édition) -->
            <div v-if="point._open && point.editMode" class="section-card section-card--standalone">
              <div class="sc-header">
                <span class="sc-title sc-title--position">{{ t('sectionPosition') }}</span>
                <span class="help-tip">?<span class="help-tip-text">{{ t('sectionPositionHint') }}</span></span>
              </div>
              <p class="position-desc">{{ t('positionDesc') }}</p>
              <div class="coord-row">
                <button class="btn-map-pick" @click="openMapPicker('point', point)">📍 {{ t('pickOnMap') }}</button>
                <button class="btn-coords-toggle" :class="{ active: point._showCoords }" :title="point._showCoords ? t('hideCoords') : t('showCoords')" @click="point._showCoords = !point._showCoords">👁</button>
              </div>
              <div v-if="point._showCoords" class="field-row mt-2">
                <div class="field-group">
                  <label class="field-label">{{ t('latitude') }}</label>
                  <input v-model="point.lat" class="form-input-sm" placeholder="32.657" />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ t('longitude') }}</label>
                  <input v-model="point.lng" class="form-input-sm" placeholder="35.417" />
                </div>
              </div>
              <template v-if="point.navigationScreen === 'dynamicMap'">
                <div class="section-divider">{{ t('waypoints') }}</div>
                <div v-for="(wp, i) in point.waypoints || []" :key="i" class="field-row">
                  <div class="field-group">
                    <label class="field-label">Waypoint {{ i + 1 }} Lat</label>
                    <input v-model="wp.lat" class="form-input-sm" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Waypoint {{ i + 1 }} Lng</label>
                    <input v-model="wp.lng" class="form-input-sm" />
                  </div>
                </div>
                <button class="btn-ghost mt-2" @click="addWaypoint(point)">{{ t('addWaypoint') }}</button>
              </template>
            </div>

          </div><!-- /card -->

          <!-- Add point buttons -->
          <!-- Points bar for subscription users -->
          <div v-if="userPlan && userPlanConfig" class="plan-points-bar">
            <span class="plan-points-count">
              📍 {{ allPoints.length }} / {{ userPlanConfig.maxPoints >= 999 ? '∞' : userPlanConfig.maxPoints }} {{ t('points') }}
            </span>
            <div class="plan-points-track">
              <div class="plan-points-fill"
                :style="{ width: Math.min(100, allPoints.length / (userPlanConfig.maxPoints || 1) * 100) + '%' }"></div>
            </div>
          </div>
          <!-- Add point buttons -->
          <div v-if="!newPoint" class="add-cards-row">
            <div v-if="!userPlan || !userPlanConfig || allPoints.length < userPlanConfig.maxPoints"
              class="card add-card" @click="openNewPointForm">
              {{ t('addPoint') }}
            </div>
            <div v-else class="card add-card add-card--limit" @click="$router.push('/pricing')">
              🔒 {{ t('planPointsLimit').replace('{max}', userPlanConfig.maxPoints) }}
              <span class="add-card--limit-link">{{ t('planUpgrade') }}</span>
            </div>
            <div v-if="isSuperAdmin" class="card add-card add-card--nav" @click="newNavPoint = true; nnp = freshNavPoint()">{{ t('addNavPoint') }}</div>
          </div>
          </template><!-- /!onboarding -->

          <!-- NEW NAV POINT FORM -->
          <div v-if="newNavPoint" class="card new-form-card">
            <div class="card-header">
              <span class="card-title">{{ t('newNavPoint') }}</span>
              <button class="btn-cancel" @click="newNavPoint = false">✕</button>
            </div>
            <div class="card-body edit-form">
              <p class="upload-hint" style="margin-bottom:12px">{{ t('navPointInfo') }}</p>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">{{ t('pointNumber') }} *</label>
                  <input v-model.number="nnp.pointNumber" type="number" class="form-input-sm" />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ t('navigationScreen') }}</label>
                  <select v-model="nnp.navigationScreen" class="form-input-sm">
                    <option value="imagesList">{{ t('screenImagesList') }}</option>
                    <option value="dynamicMap">{{ t('screenDynamicMap') }}</option>
                    <option value="missingWord">{{ t('screenMissingWord') }}</option>
                  </select>
                </div>
              </div>

              <div class="field-group mt-2">
                <label class="field-label">{{ t('image') }} *</label>
                <div class="upload-hint">{{ t('imageHint') }}</div>
                <input type="file" accept="image/*" class="form-input-sm mt-1" @change="onNavPointImagePicked" />
                <div v-if="nnp.imagePreview" class="thumb-row">
                  <img :src="nnp.imagePreview" class="point-thumb mt-2" alt="" />
                </div>
                <div v-if="nnp.lat || nnp.lng" class="gps-badge">📍 GPS: {{ nnp.lat }}, {{ nnp.lng }}</div>
              </div>

              <div v-if="nnp.navigationScreen === 'missingWord'" class="field-group mt-2">
                <label class="field-label">{{ t('screenMissingWord') }}</label>
                <input v-model="nnp.missingWord" class="form-input-sm" />
              </div>

              <hr class="section-hr" />
              <div class="coord-row">
                <button class="btn-map-pick" @click="openMapPicker('nnp')">{{ t('pickOnMap') }}</button>
                <button class="btn-coords-toggle" :class="{ active: showNnpCoords }" :title="showNnpCoords ? t('hideCoords') : t('showCoords')" @click="showNnpCoords = !showNnpCoords">👁</button>
              </div>
              <div v-if="showNnpCoords" class="field-row mt-2">
                <div class="field-group">
                  <label class="field-label">{{ t('latitude') }} *</label>
                  <input v-model="nnp.lat" class="form-input-sm" placeholder="32.657" />
                </div>
                <div class="field-group">
                  <label class="field-label">{{ t('longitude') }} *</label>
                  <input v-model="nnp.lng" class="form-input-sm" placeholder="35.417" />
                </div>
              </div>

              <div class="row-buttons mt-4">
                <button class="btn-secondary" @click="newNavPoint = false">{{ t('cancel') }}</button>
                <button class="btn-success" :disabled="loading" @click="submitNewNavPoint">
                  <span v-if="loading">{{ t('saving') }}</span>
                  <span v-else>{{ t('save') }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- NEW POINT WIZARD -->
          <div v-if="newPoint" class="card new-form-card card--editing">
            <div class="card-header">
              <div>
                <span class="card-title">{{ t('newPoint') }} #{{ np.pointNumber }}</span>
                <div v-if="onboarding" class="onboarding-counter">{{ allPoints.length + 1 }} / 4</div>
              </div>
              <div style="display:flex;align-items:center;gap:12px">
                <div class="wiz-steps-row">
                  <span :class="['wiz-dot', wizardStep >= 1 ? 'wiz-dot-active' : '']">1</span>
                  <span class="wiz-dot-line"></span>
                  <span :class="['wiz-dot', wizardStep >= 2 ? 'wiz-dot-active' : '']">2</span>
                  <span class="wiz-dot-line"></span>
                  <span :class="['wiz-dot', wizardStep >= 3 ? 'wiz-dot-active' : '']">3</span>
                </div>
                <button class="btn-cancel" @click="newPoint = false; resetNewPoint(); wizardStep = 1; onboarding = false; localStorage.setItem('efoani_onboarded_' + navigationName, '1')">✕</button>
              </div>
            </div>
            <div v-if="onboarding" class="onboarding-alert">
              {{ t('onboardingAlert') }}
            </div>

            <!-- ── STEP 1: Navigation Screen ── -->
            <div v-if="wizardStep === 1" class="card-body">
              <div class="wiz-step-label">{{ t('wizStep1Title') }}</div>
              <div class="wiz-step-hint">{{ t('wizStep1Hint') }}</div>
              <div class="wiz-options-grid">
                <div v-if="hasFeature('imageList')" :class="['wiz-opt', np.navigationScreen === 'imagesList' ? 'wiz-opt--selected' : '']"
                  @click="np.navigationScreen = 'imagesList'">
                  <div class="wiz-opt-icon">🖼️</div>
                  <div class="wiz-opt-name">{{ t('wizOptImageListName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptImageListDesc') }}</div>
                </div>
                <div v-if="hasFeature('dynamicMap')" :class="['wiz-opt', np.navigationScreen === 'dynamicMap' ? 'wiz-opt--selected' : '']"
                  @click="np.navigationScreen = 'dynamicMap'">
                  <div class="wiz-opt-icon">🗺️</div>
                  <div class="wiz-opt-name">{{ t('screenDynamicMap') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptDynamicMapDesc') }}</div>
                </div>
                <div v-if="hasFeature('carousel')" :class="['wiz-opt', np.navigationScreen === 'carousel' ? 'wiz-opt--selected' : '']"
                  @click="np.navigationScreen = 'carousel'">
                  <div class="wiz-opt-icon">📸</div>
                  <div class="wiz-opt-name">{{ t('screenCarousel') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptCarouselDesc') }}</div>
                </div>
                <div v-if="hasFeature('missingWord')" :class="['wiz-opt', np.navigationScreen === 'missingWord' ? 'wiz-opt--selected' : '']"
                  @click="np.navigationScreen = 'missingWord'">
                  <div class="wiz-opt-icon">🔤</div>
                  <div class="wiz-opt-name">{{ t('screenMissingWord') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptMissingWordDesc') }}</div>
                </div>
              </div>

              <div v-if="np.navigationScreen === 'imagesList' || np.navigationScreen === 'missingWord'" class="wiz-field-section mt-3">
                <label class="field-label">{{ t('wizPhotoLabel') }}</label>
                <div class="upload-hint">
                  <span v-if="np.navigationScreen === 'imagesList'">{{ t('wizImageListHint') }}</span>
                  <span v-else>{{ t('wizMissingWordImageHint') }}</span>
                </div>
                <input type="file" accept="image/*" class="form-input-sm mt-1" ref="npFileInput" @change="onNewPointImagePicked" />
                <div v-if="np.imagePreview" class="thumb-row">
                  <img :src="np.imagePreview" class="point-thumb mt-2" alt="" />
                  <button class="btn-edit-photo" @click="openPhotoEditor(np)">{{ t('editPhoto') }}</button>
                </div>
              </div>
              <div v-if="np.navigationScreen === 'missingWord'" class="wiz-field-section mt-2">
                <label class="field-label">{{ t('wizMissingWordFieldLabel') }}</label>
                <div class="upload-hint">{{ t('wizMissingWordFieldHint') }}</div>
                <input v-model="np.missingWord" class="form-input-sm mt-1" :placeholder="t('wizMissingWordPlaceholder')" />
              </div>

              <div class="wiz-field-section mt-3">
                <label class="field-label">{{ t('wizMapPositionLabel') }}</label>
                <div class="coord-row mt-1">
                  <button class="btn-map-pick" style="flex:1" @click="openMapPicker('np')">{{ t('wizPickOnMapBtn') }}</button>
                  <button class="btn-coords-toggle" :class="{ active: showNpCoords }" @click="showNpCoords = !showNpCoords">👁</button>
                </div>
                <div v-if="np.lat || np.lng" class="gps-badge mt-1">📍 GPS: {{ np.lat }}, {{ np.lng }}</div>
                <div v-if="showNpCoords" class="field-row mt-2">
                  <div class="field-group">
                    <label class="field-label">{{ t('latitude') }} *</label>
                    <input v-model="np.lat" class="form-input-sm" placeholder="32.657" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">{{ t('longitude') }} *</label>
                    <input v-model="np.lng" class="form-input-sm" placeholder="35.417" />
                  </div>
                </div>
              </div>

              <div class="row-buttons mt-4">
                <button class="btn-secondary" @click="newPoint = false; resetNewPoint(); wizardStep = 1; onboarding = false; localStorage.setItem('efoani_onboarded_' + navigationName, '1')">{{ t('cancel') }}</button>
                <button class="btn-success" @click="wizardStep = 2; scrollTop()">{{ t('wizNext') }}</button>
              </div>
            </div>

            <!-- ── STEP 2: Information ── -->
            <div v-if="wizardStep === 2" class="card-body">
              <div class="wiz-step-label">{{ t('wizStep2Title') }}</div>
              <div class="wiz-step-hint">{{ t('wizStep2Hint') }}</div>
              <div class="wiz-options-grid">
                <div :class="['wiz-opt', !np.withText && !np.withAudio && !np.withVideo && !np.withAR && !np.withPhone ? 'wiz-opt--selected' : '']"
                  @click="np.withText = false; np.withAudio = false; np.withVideo = false; np.withAR = false; np.withPhone = false">
                  <div class="wiz-opt-icon">⏭️</div>
                  <div class="wiz-opt-name">{{ t('wizOptNoInfoName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptNoInfoDesc') }}</div>
                </div>
                <div v-if="hasFeature('texte')" :class="['wiz-opt', np.withText ? 'wiz-opt--selected' : '']" @click="np.withText = !np.withText">
                  <div class="wiz-opt-icon">📝</div>
                  <div class="wiz-opt-name">{{ t('labelText') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptTextDesc') }}</div>
                </div>
                <div v-if="hasFeature('audio')" :class="['wiz-opt', np.withAudio ? 'wiz-opt--selected' : '']" @click="np.withAudio = !np.withAudio">
                  <div class="wiz-opt-icon">🎵</div>
                  <div class="wiz-opt-name">{{ t('labelAudio') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptAudioDesc') }}</div>
                </div>
                <div v-if="hasFeature('video')" :class="['wiz-opt', np.withVideo ? 'wiz-opt--selected' : '']" @click="np.withVideo = !np.withVideo">
                  <div class="wiz-opt-icon">🎬</div>
                  <div class="wiz-opt-name">{{ t('labelVideo') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptVideoDesc') }}</div>
                </div>
                <div v-if="hasFeature('ar')" :class="['wiz-opt', np.withAR ? 'wiz-opt--selected' : '']" @click="np.withAR = !np.withAR">
                  <div class="wiz-opt-icon">🔮</div>
                  <div class="wiz-opt-name">{{ t('wizOptARName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptARDesc') }}</div>
                </div>
                <div v-if="hasFeature('phone')" :class="['wiz-opt', np.withPhone ? 'wiz-opt--selected' : '']" @click="np.withPhone = !np.withPhone">
                  <div class="wiz-opt-icon">📞</div>
                  <div class="wiz-opt-name">{{ t('wizOptPhoneName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptPhoneDesc') }}</div>
                </div>
              </div>

              <div v-if="np.withText" class="wiz-field-section mt-3">
                <label class="field-label">{{ t('wizTextInfoLabel') }}</label>
                <textarea v-model="np.pointInformation" class="form-textarea" rows="4" :placeholder="t('wizTextInfoPlaceholder')"></textarea>
              </div>
              <div v-if="np.withAudio" class="wiz-field-section mt-3">
                <label class="field-label">{{ t('wizAudioLabel') }}</label>
                <input v-model="np.audioUrl" class="form-input-sm" />
              </div>
              <div v-if="np.withVideo" class="wiz-field-section mt-3">
                <label class="field-label">{{ t('wizVideoLabel') }} <span v-if="np.withVerticalVideo" style="color:#f59e0b;font-size:11px">{{ t('shortsDetected') }}</span></label>
                <input :value="np.videoUrl" class="form-input-sm" placeholder="https://youtube.com/... ou ID vidéo"
                  @input="e => parseVideoInput(e.target.value, np)" />
              </div>
              <div v-if="np.withPhone" class="wiz-field-section mt-3">
                <label class="field-label">{{ t('wizCallerNameLabel') }}</label>
                <input v-model="np.callerName" class="form-input-sm" :placeholder="t('callerNamePlaceholder')" />
                <label class="field-label mt-2">{{ t('phoneAudioUrl') }}</label>
                <input v-model="np.phoneAudioUrl" class="form-input-sm" placeholder="https://…" />
              </div>
              <div v-if="np.withAR" class="wiz-field-section mt-3">
                <label class="field-label mt-1">{{ t('arDirection') }}</label>
                <div class="ar-dir-toggle mt-1">
                  <button :class="['ar-dir-btn', !np.arDirectionExact ? 'active' : '']" @click="np.arDirectionExact = false">{{ t('arPreset') }}</button>
                  <button :class="['ar-dir-btn', np.arDirectionExact ? 'active' : '']" @click="np.arDirectionExact = true">{{ t('arExactDeg') }}</button>
                </div>
                <select v-if="!np.arDirectionExact" v-model.number="np.arDirection" class="form-input-sm mt-1">
                  <option :value="0">{{ t('arNorth') }}</option><option :value="45">{{ t('arNE') }}</option>
                  <option :value="90">{{ t('arEast') }}</option><option :value="135">{{ t('arSE') }}</option>
                  <option :value="180">{{ t('arSouth') }}</option><option :value="225">{{ t('arSW') }}</option>
                  <option :value="270">{{ t('arWest') }}</option><option :value="315">{{ t('arNW') }}</option>
                </select>
                <div v-else class="ar-deg-input mt-1">
                  <input v-model.number="np.arDirection" type="number" min="0" max="359" class="form-input-sm" placeholder="0–359" />
                  <span class="ar-deg-unit">°</span>
                </div>
                <label class="field-label mt-2">{{ t('arEffectUrl') }}</label>
                <input v-model="np.arVideoUrl" class="form-input-sm" placeholder="https://..." />
                <label class="field-label mt-2">{{ t('arSearchWord') }}</label>
                <input v-model="np.arSearchWord" class="form-input-sm" :placeholder="t('arSearchWordPlaceholder')" />
                <div class="field-hint">{{ t('arSearchWordHint') }}</div>
                <div class="ar-gif-options">
                  <div class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arGifCount') }}</label>
                    <select v-model.number="np.arGifCount" class="form-input-sm">
                      <option :value="1">{{ t('arGif1') }}</option><option :value="2">{{ t('arGif2') }}</option><option :value="3">3</option>
                    </select>
                  </div>
                  <div v-if="np.arGifCount > 1" class="field-group" style="flex:1">
                    <label class="field-label mt-2">{{ t('arSpacing') }}</label>
                    <input v-model.number="np.arGifSpacing" type="number" class="form-input-sm" placeholder="0" />
                    <div class="field-hint">{{ t('arSpacingHint') }}</div>
                  </div>
                </div>
                <label class="field-label mt-2">{{ t('arGrowAnimation') }}</label>
                <div class="ar-anim-toggle">
                  <button :class="['ar-dir-btn', np.arGrowAnimation !== false ? 'active' : '']" @click="np.arGrowAnimation = true">{{ t('arGrow') }}</button>
                  <button :class="['ar-dir-btn', np.arGrowAnimation === false ? 'active' : '']" @click="np.arGrowAnimation = false">{{ t('arAppear') }}</button>
                </div>
              </div>

              <div class="row-buttons mt-4">
                <button class="btn-secondary" @click="wizardStep = 1; scrollTop()">{{ t('wizBack') }}</button>
                <button class="btn-success" @click="wizardStep = 3; scrollTop()">{{ t('wizNext') }}</button>
              </div>
            </div>

            <!-- ── STEP 3: Exercise ── -->
            <div v-if="wizardStep === 3" class="card-body">
              <div class="wiz-step-label">{{ t('wizStep3Title') }}</div>
              <div class="wiz-step-hint">{{ t('wizStep3Hint') }}</div>
              <div class="wiz-options-grid">
                <div v-if="hasFeature('QCM')" :class="['wiz-opt', np.picked === 'questions' ? 'wiz-opt--selected' : '']" @click="np.picked = 'questions'">
                  <div class="wiz-opt-icon">❓</div><div class="wiz-opt-name">{{ t('wizOptQuestionsName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptQuestionsDesc') }}</div>
                </div>
                <div v-if="hasFeature('puzzle')" :class="['wiz-opt', np.picked === 'puzzle' ? 'wiz-opt--selected' : '']" @click="np.picked = 'puzzle'">
                  <div class="wiz-opt-icon">🧩</div><div class="wiz-opt-name">{{ t('exercisePuzzle') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptPuzzleDesc') }}</div>
                </div>
                <div v-if="hasFeature('multipleMatch')" :class="['wiz-opt', np.picked === 'multiplematch' ? 'wiz-opt--selected' : '']" @click="np.picked = 'multiplematch'">
                  <div class="wiz-opt-icon">🔗</div><div class="wiz-opt-name">{{ t('wizOptMultipleMatchName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptMultipleMatchDesc') }}</div>
                </div>
                <div v-if="hasFeature('memory')" :class="['wiz-opt', np.picked === 'memory' ? 'wiz-opt--selected' : '']" @click="np.picked = 'memory'">
                  <div class="wiz-opt-icon">🃏</div><div class="wiz-opt-name">{{ t('exerciseMemory') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptMemoryDesc') }}</div>
                </div>
                <div v-if="hasFeature('trueFalse')" :class="['wiz-opt', np.picked === 'justefaux' ? 'wiz-opt--selected' : '']" @click="np.picked = 'justefaux'">
                  <div class="wiz-opt-icon">✅</div><div class="wiz-opt-name">{{ t('wizOptTrueFalseName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptTrueFalseDesc') }}</div>
                </div>
                <div v-if="hasFeature('motManquant')" :class="['wiz-opt', np.picked === 'motManquant' ? 'wiz-opt--selected' : '']" @click="np.picked = 'motManquant'">
                  <div class="wiz-opt-icon">🔠</div><div class="wiz-opt-name">{{ t('wizOptFillBlanksName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptFillBlanksDesc') }}</div>
                </div>
                <div v-if="hasFeature('rushHour')" :class="['wiz-opt', np.picked === 'rushhour' ? 'wiz-opt--selected' : '']" @click="np.picked = 'rushhour'">
                  <div class="wiz-opt-icon">🚗</div><div class="wiz-opt-name">{{ t('exerciseRushHour') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptRushHourDesc') }}</div>
                </div>
                <div v-if="hasFeature('signcard')" :class="['wiz-opt', np.picked === 'signcard' ? 'wiz-opt--selected' : '']" @click="np.picked = 'signcard'">
                  <div class="wiz-opt-icon">🪧</div><div class="wiz-opt-name">{{ t('wizOptSignCardName') }}</div>
                  <div class="wiz-opt-desc">{{ t('wizOptSignCardDesc') }}</div>
                </div>
              </div>

              <div v-if="np.picked === 'questions'" class="wiz-field-section mt-3">
                <div class="section-divider">{{ t('questionsTitle') }}</div>
                <div v-for="(q, i) in np._questions" :key="i" class="pq-card">
                  <div class="pq-card-header">
                    <span class="pq-text">{{ q.question }}</span>
                    <button class="btn-cancel" @click="np._questions.splice(i, 1)">✕</button>
                  </div>
                  <div class="pq-body">
                    <div class="answer correct">✅ {{ q.correctAnswer }}</div>
                    <div v-if="q.wrongAnswer1" class="answer wrong">✗ {{ q.wrongAnswer1 }}</div>
                    <div v-if="q.wrongAnswer2" class="answer wrong">✗ {{ q.wrongAnswer2 }}</div>
                    <div v-if="q.wrongAnswer3" class="answer wrong">✗ {{ q.wrongAnswer3 }}</div>
                  </div>
                </div>
                <div v-if="np._addingQuestion" class="pq-card pq-new">
                  <div class="field-group"><label class="field-label">{{ t('jfQuestion') }} *</label>
                    <textarea v-model="np._nq.question" class="form-textarea" rows="2"></textarea></div>
                  <div class="field-group"><label class="field-label">{{ t('correctAnswer') }} *</label>
                    <input v-model="np._nq.correctAnswer" class="form-input-sm correct-input" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer1') }}</label>
                    <input v-model="np._nq.wrongAnswer1" class="form-input-sm" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer2') }}</label>
                    <input v-model="np._nq.wrongAnswer2" class="form-input-sm" /></div>
                  <div class="field-group"><label class="field-label">{{ t('wrongAnswer3') }}</label>
                    <input v-model="np._nq.wrongAnswer3" class="form-input-sm" /></div>
                  <div class="row-buttons mt-3">
                    <button class="btn-secondary" @click="np._addingQuestion = false; np._nq = freshQuestion()">{{ t('cancel') }}</button>
                    <button class="btn-success" @click="addQuestionToNewPoint">{{ t('addQuestion') }}</button>
                  </div>
                </div>
                <button v-else class="pqs-add-btn" @click="np._addingQuestion = true">{{ t('addQuestion') }}</button>
              </div>

              <div v-if="np.picked === 'puzzle'" class="field-group wiz-field-section mt-3">
                <label class="field-label">🧩 {{ t('puzzleImage') }}</label>
                <div class="upload-hint">{{ t('puzzleImageHint') }}</div>
                <div v-if="np.puzzleImagePreview" class="thumb-row mt-2 mb-1">
                  <img :src="np.puzzleImagePreview" class="puzzle-thumb" alt="" />
                </div>
                <label class="file-btn mt-1">
                  {{ np.puzzleImagePreview ? t('changeImage') : t('chooseImage') }}
                  <input type="file" accept="image/*" style="display:none" @change="onNewPointPuzzleImagePicked" />
                </label>
              </div>

              <div v-if="np.picked === 'memory'" class="field-group wiz-field-section mt-3">
                <div class="section-divider">{{ t('memoryImages') }}</div>
                <div class="upload-hint">{{ t('memoryImagesHint') }}</div>
                <label class="file-btn mt-1">
                  {{ t('addImage') }} ({{ (np.memoryImages || []).length }}/6)
                  <input v-if="(np.memoryImages || []).length < 6" type="file" accept="image/*" style="display:none" @change="e => onMemoryImagePicked(e, np)" />
                </label>
                <div class="mem-img-preview-row">
                  <div v-for="(img, i) in (np.memoryImages || [])" :key="i" class="mem-img-preview">
                    <img :src="img" alt="" /><button class="mem-img-remove" @click="np.memoryImages.splice(i, 1)">✕</button>
                  </div>
                </div>
              </div>

              <div v-if="np.picked === 'multiplematch'" class="wiz-field-section mt-3">
                <div class="section-divider">{{ t('mmTitle') }}</div>
                <div class="upload-hint">{{ t('mmHint') }}</div>
                <div v-for="n in 4" :key="n" class="mm-pair-edit">
                  <div class="mm-pair-header">{{ t('mmPair') }} {{ n }}</div>
                  <div class="mm-pair-row">
                    <div class="mm-img-col">
                      <input type="file" accept="image/*" class="form-input-sm" @change="e => onNewPointMmImagePicked(e, n)" />
                      <img v-if="np['itemImage' + n]" :src="np['itemImage' + n]" class="mm-thumb mt-1" alt="" />
                      <button v-if="np['itemImage' + n]" class="btn-edit-photo mt-1" @click="openPhotoEditor(np, 'itemImage' + n)">{{ t('editPhoto') }}</button>
                    </div>
                    <div class="mm-label-col">
                      <label class="field-label">{{ t('mmLabel') }}</label>
                      <input v-model="np['description' + n]" class="form-input-sm" :placeholder="t('mmLabelPlaceholder') + ' ' + n" />
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="np.picked === 'motManquant'" class="field-group wiz-field-section mt-3">
                <div class="section-divider">{{ t('exerciseMotManquant') }}</div>
                <div class="upload-hint">{{ t('mmqHint') }}</div>
                <textarea v-model="np.motManquantText" class="form-textarea" rows="5" :placeholder="t('mmqPlaceholder')" />
                <div v-if="np.motManquantText" class="field-hint">
                  {{ (np.motManquantText.match(/\{[^}]+\}/g) || []).length }} {{ t('mmqHoles') }} :
                  {{ (np.motManquantText.match(/\{([^}]+)\}/g) || []).map(m => m.slice(1,-1)).join(', ') }}
                </div>
                <label class="field-label mt-2">{{ t('mmqMode') }}</label>
                <div class="mm-mode-toggle">
                  <button :class="['mm-mode-btn', (np.motManquantMode || 'visible') === 'visible' ? 'mm-mode-active' : '']"
                    @click="np.motManquantMode = 'visible'; $forceUpdate()">{{ t('mmqVisible') }}</button>
                  <button :class="['mm-mode-btn', np.motManquantMode === 'hidden' ? 'mm-mode-active' : '']"
                    @click="np.motManquantMode = 'hidden'; $forceUpdate()">{{ t('mmqHidden') }}</button>
                </div>
              </div>

              <div v-if="np.picked === 'justefaux'" class="field-group wiz-field-section mt-3">
                <div class="section-divider">{{ t('jfTitle') }}</div>
                <label class="field-label">{{ t('jfQuestion') }}</label>
                <input v-model="np.justeFauxQuestion" class="form-input-sm" :placeholder="t('jfQuestionPlaceholder')" />
                <label class="field-label mt-2">{{ t('jfOptions') }}</label>
                <div v-for="(item, idx) in normalizeJFItems(np.justeFauxItems)" :key="idx" class="jf-admin-row">
                  <input v-model="item.label" class="form-input-sm jf-label-input" :placeholder="t('jfOptionPlaceholder')" />
                  <button :class="['jf-toggle-btn', item.correct === true ? 'jf-active-vrai' : '']" @click="item.correct = true; $forceUpdate()">✓</button>
                  <button :class="['jf-toggle-btn', item.correct !== true ? 'jf-active-faux' : '']" @click="item.correct = false; $forceUpdate()">✗</button>
                  <button class="jf-remove-btn" @click="removeJusteFauxItem(np, idx)">🗑</button>
                </div>
                <button class="btn-ghost mt-2" @click="addJusteFauxItem(np)">{{ t('jfAddOption') }}</button>
              </div>

              <div v-if="np.picked === 'signcard'" class="field-group wiz-field-section mt-3">
                <div class="section-divider">{{ t('signCardTitle') }}</div>
                <label class="field-label">{{ t('signCardImage') }}</label>
                <input type="file" accept="image/*" class="form-input-sm mt-1" @change="onNewSignCardImagePicked" />
                <div v-if="np.signCardImagePreview" class="sc-admin-preview-wrap mt-2">
                  <img :src="np.signCardImagePreview" class="sc-admin-img" />
                  <div class="sc-admin-zone" :style="signCardZoneStyle(np)">{{ t('signCardZone') }}</div>
                </div>
                <label class="field-label mt-2">{{ t('signCardHeight') }} {{ np.signCardSigHeightPct || 20 }}%</label>
                <input type="range" min="5" max="50" step="5" class="sc-range mt-1"
                  :value="np.signCardSigHeightPct || 20"
                  @input="np.signCardSigHeightPct = +$event.target.value; $forceUpdate()" />
                <label class="field-label mt-2">{{ t('signCardVAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="v in [{k:'top',tKey:'alignTop'},{k:'center',tKey:'alignCenter'},{k:'bottom',tKey:'alignBottom'}]" :key="v.k"
                    :class="['sc-align-btn', (np.signCardSigVAlign || 'bottom') === v.k ? 'sc-align-active' : '']"
                    @click="np.signCardSigVAlign = v.k; $forceUpdate()">{{ t(v.tKey) }}</button>
                </div>
                <label class="field-label mt-2">{{ t('signCardHAlign') }}</label>
                <div class="sc-align-row mt-1">
                  <button v-for="h in [{k:'left',tKey:'alignLeft'},{k:'center',tKey:'alignCenter'},{k:'right',tKey:'alignRight'}]" :key="h.k"
                    :class="['sc-align-btn', (np.signCardSigHAlign || 'center') === h.k ? 'sc-align-active' : '']"
                    @click="np.signCardSigHAlign = h.k; $forceUpdate()">{{ t(h.tKey) }}</button>
                </div>
              </div>

              <div v-if="np.picked === 'rushhour'" class="field-group wiz-field-section mt-3">
                <div class="section-divider">{{ t('rushHourTitle') }}</div>
                <RushHourEditor v-model="np.rushHourVehicles" />
              </div>

              <div class="row-buttons mt-4">
                <button class="btn-secondary" @click="wizardStep = 2; scrollTop()">{{ t('wizBack') }}</button>
                <button class="btn-success" :disabled="loading" @click="submitNewPoint">
                  <span v-if="loading">{{ t('saving') }}</span>
                  <span v-else>{{ t('wizSavePoint') }} {{ np.pointNumber }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ─────────────────── TAB: SETTINGS ─────────────────── -->
      <div v-show="activeTab === 'settings'" class="tab-content">

        <!-- Paramètres de la carte — obligatoire pour tous -->
        <div class="settings-section">
          <div class="settings-title">{{ t('settingsMap') }} <span class="settings-required">*</span></div>
          <p class="upload-hint">{{ t('settingsMapHint') }}</p>
          <div class="coord-row mt-2">
            <button class="btn-map-pick" @click="openMapPicker('settings')">{{ t('settingsPickOnMap') }}</button>
            <button class="btn-coords-toggle" :class="{ active: showSettingsCoords }" :title="showSettingsCoords ? 'Masquer lat/lng' : 'Voir lat/lng'" @click="showSettingsCoords = !showSettingsCoords">👁</button>
          </div>
          <div v-if="settings.mapLat && settings.mapLng" class="settings-coords-badge mt-2">
            📍 {{ Number(settings.mapLat).toFixed(5) }}, {{ Number(settings.mapLng).toFixed(5) }}
          </div>
          <div v-else class="settings-coords-missing mt-2">{{ t('settingsCoordsUndefined') }}</div>
          <div v-if="showSettingsCoords" class="field-row mt-2">
            <div class="field-group">
              <label class="field-label">{{ t('centerLat') }}</label>
              <input v-model="settings.mapLat" :class="['form-input-sm', { 'input-error': !settings.mapLat }]" placeholder="32.657" />
            </div>
            <div class="field-group">
              <label class="field-label">{{ t('centerLng') }}</label>
              <input v-model="settings.mapLng" :class="['form-input-sm', { 'input-error': !settings.mapLng }]" placeholder="35.417" />
            </div>
          </div>
          <div class="field-row mt-2" style="max-width:140px">
            <div class="field-group">
              <label class="field-label">{{ t('zoom') }}</label>
              <input v-model.number="settings.mapZoom" type="number" class="form-input-sm" placeholder="15" min="1" max="22" />
            </div>
          </div>
          <button class="btn-success mt-3" :disabled="!settingsDirty('mapLat', 'mapLng', 'mapZoom')" @click="saveMapParameters">{{ t('saveMap') }}</button>
        </div>

        <!-- Image de fond — plan pro uniquement -->
        <div v-if="hasFeature('bgImage')" class="settings-section">
          <div class="settings-title">{{ t('bgImage') }}</div>
          <p class="upload-hint">{{ t('bgImageHint') }}</p>
          <div v-if="settings.bgImagePreview || settings.bgImageUrl" class="thumb-row mb-2">
            <img :src="settings.bgImagePreview || settings.bgImageUrl" style="max-width:100%;border-radius:10px;max-height:180px;object-fit:cover;" alt="" />
          </div>
          <label class="file-btn">
            {{ settings.bgImageUrl ? t('changeImage') : t('chooseImage') }}
            <input type="file" accept="image/*" style="display:none" @change="onBgImagePicked" />
          </label>
          <button v-if="settings.bgImageFile" class="btn-success mt-3" :disabled="settings.bgImageSaving" @click="saveBgImage">
            {{ settings.bgImageSaving ? t('saving') : t('saveBgImage') }}
          </button>
        </div>

        <!-- Nom d'affichage -->
        <div v-if="hasFeature('displayName')" class="settings-section">
          <div class="settings-title">{{ t('displayName') }}</div>
          <p class="upload-hint">{{ t('displayNameHint') }}</p>
          <input v-model="settings.displayName" class="form-input-sm" :placeholder="navigationName" />
          <button class="btn-success mt-3" :disabled="!settingsDirty('displayName')" @click="saveDisplayName">{{ t('saveDisplayName') }}</button>
        </div>

        <!-- Lieu de départ -->
        <div v-if="hasFeature('startLocation')" class="settings-section">
          <div class="settings-title">{{ t('settingsStartLocation') }}</div>
          <p class="upload-hint">{{ t('startLocationHint') }}</p>
          <input v-model="settings.startLocationName" class="form-input-sm" :placeholder="t('startLocationPlaceholder')" />
          <button class="btn-success mt-3" :disabled="!settingsDirty('startLocationName')" @click="saveStartLocationName">{{ t('saveStartLocation') }}</button>
        </div>

        <!-- Dernier point — toujours visible -->
        <div class="settings-section">
          <div class="settings-title">{{ t('settingsLastPoint') }}</div>
          <p class="upload-hint">{{ t('lastPointHint') }}</p>
          <input v-model.number="settings.lastPoint" type="number" :class="['form-input-sm', { 'input-error': !settings.lastPoint }]" :placeholder="t('lastPointPlaceholder')" min="1" />
          <button class="btn-success mt-3" :disabled="!settingsDirty('lastPoint')" @click="saveLastPoint">{{ t('saveLastPoint') }}</button>
        </div>

        <!-- URL du jeu — toujours visible -->
        <div class="settings-section">
          <div class="settings-title">{{ t('gameUrl') }}</div>
          <div class="url-display">
            <span class="url-text">{{ gameUrl }}</span>
            <button class="btn-copy-url" @click="copyGameUrl">{{ t('copy') }}</button>
          </div>
        </div>

        <!-- Lien terrain -->
        <div v-if="hasFeature('fieldLink')" class="settings-section">
          <div class="settings-title">{{ t('fieldLinkTitle') }}</div>
          <p class="upload-hint">{{ t('fieldLinkHint') }}</p>
          <div v-if="fieldLink" class="url-display mt-2">
            <span class="url-text" style="font-size:12px">{{ fieldLink }}</span>
            <button class="btn-copy-url" @click="copyFieldLink">📋</button>
          </div>
          <button class="btn-success mt-3" :disabled="fieldLinkLoading" @click="generateFieldLink">
            <span v-if="fieldLinkLoading">{{ t('fieldLinkGenerating') }}</span>
            <span v-else>{{ t('fieldLinkGenerate') }}</span>
          </button>
        </div>

        <div class="settings-section">
          <div class="settings-title">{{ t('settingsOrders') }}</div>
          <p class="help-text">{{ t('ordersHint') }}</p>
          <div v-if="settings.pointsOrders.length === 0" class="muted mb-2">{{ t('noOrders') }}</div>
          <div v-for="(order, i) in settings.pointsOrders" :key="order.key || i" class="order-row">
            <span class="order-index">{{ t('order') }} {{ i + 1 }}</span>
            <input v-model="order.str" class="form-input-sm" placeholder="1, 2, 3, 4, 5" />
            <button class="btn-danger-sm" @click="deleteOrder(order, i)">🗑️</button>
          </div>
          <div class="row-buttons mt-2">
            <button class="btn-ghost" @click="settings.pointsOrders.push({ key: null, str: '' })">{{ t('addOrder') }}</button>
            <button class="btn-success" :disabled="!settingsDirty('pointsOrders')" @click="saveAllPointsOrders">{{ t('saveOrders') }}</button>
          </div>
        </div>

        <div v-if="isSuperAdmin" class="settings-section">
          <div class="settings-title">{{ t('settingsOptions') }}</div>
          <div class="checkbox-grid">
            <label class="check-label"><input type="checkbox" v-model="settings.randomQuestions" /> {{ t('randomQuestions') }}</label>
          </div>
          <div class="field-group mt-3">
            <label class="field-label">{{ t('settingsMode') }}</label>
            <div class="mode-cards">
              <div
                :class="['mode-card', { active: settings.questionsOrder === 'linkedToPointNumber' }]"
                @click="settings.questionsOrder = 'linkedToPointNumber'">
                <div class="mode-card-icon">📍</div>
                <div class="mode-card-title">{{ t('modeGeographic') }}</div>
                <div class="mode-card-desc">{{ t('modeGeographicFull') }}</div>
              </div>
              <div
                :class="['mode-card', { active: settings.questionsOrder === 'sorted' }]"
                @click="settings.questionsOrder = 'sorted'">
                <div class="mode-card-icon">📖</div>
                <div class="mode-card-title">{{ t('modeStory') }}</div>
                <div class="mode-card-desc">{{ t('modeStoryFull') }}</div>
              </div>
            </div>
          </div>
          <!-- Validation warning for Géographique mode -->
          <div v-if="gameModeWarnings.length" class="mode-warning-box">
            <strong>{{ t('emptyPoints') }}</strong>
            <span v-for="w in gameModeWarnings" :key="w" class="mode-warning-badge">{{ w }}</span>
            <div style="font-size:12px;margin-top:4px;color:#7f4400">{{ t('emptyPointsHint') }}</div>
          </div>
          <button class="btn-success mt-3" :disabled="!settingsDirty('questionsOrder', 'randomQuestions')" @click="saveGameMode">{{ t('saveMode') }}</button>
        </div>

        <div v-if="isSuperAdmin && settings.questionsOrder === 'sorted'" class="settings-section">
          <div class="settings-title">{{ t('storyModeTitle') }}</div>
          <p class="upload-hint">{{ t('storyModeDesc') }}</p>
          <p class="upload-hint" style="margin-top:6px">{{ t('storyModeEdit') }}</p>
        </div>

        <div class="settings-section">
          <div class="settings-title">{{ t('settingsLangs') }}</div>
          <p class="help-text">{{ t('langsHint') }}</p>
          <div class="checkbox-grid">
            <label class="check-label"><input type="checkbox" value="he" v-model="settings.languages" /> {{ t('langHebrew') }}</label>
            <label class="check-label"><input type="checkbox" value="ar" v-model="settings.languages" /> {{ t('langArabic') }}</label>
            <label class="check-label"><input type="checkbox" value="fr" v-model="settings.languages" /> {{ t('langFrench') }}</label>
            <label class="check-label"><input type="checkbox" value="en" v-model="settings.languages" /> {{ t('langEnglish') }}</label>
          </div>
          <button class="btn-success mt-3" :disabled="!settingsDirty('languages')" @click="saveLanguages">{{ t('saveLangs') }}</button>
        </div>

      </div>
    </div>

    <div v-if="toast.show" :class="['toast-msg', toast.type]">{{ toast.text }}</div>

    <!-- ═══ PHOTO EDITOR ═══════════════════════════════════════════ -->
    <PhotoEditor
      v-if="photoEditor.open"
      :src="photoEditor.src"
      :lang="adminLang"
      @save="onPhotoEditorSave"
      @cancel="photoEditor.open = false"
    />

    <!-- ═══ MAP PICKER MODAL ═══════════════════════════════════════ -->
    <div v-if="mapPicker.open" class="map-picker-overlay" @click.self="mapPicker.open = false">
      <div class="map-picker-modal">
        <div class="map-picker-header">
          <span>{{ t('mapPickerHint') }}</span>
          <button class="map-picker-close" @click="mapPicker.open = false">✕</button>
        </div>
        <div id="map-picker-container" style="width:100%;height:450px;"></div>
        <div class="map-picker-footer">
          <span v-if="mapPicker.selected" class="map-picker-coords">
            Lat: {{ mapPicker.selected.lat }}, Lng: {{ mapPicker.selected.lng }}
          </span>
          <span v-else class="map-picker-hint">{{ t('mapPickerHint') }}</span>
          <button class="btn-success" :disabled="!mapPicker.selected" @click="applyMapCoords">{{ t('confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- IMAGE CROPPER -->
    <ImageCropper
      v-if="imageCropper.show"
      :src="imageCropper.src"
      :lang="adminLang"
      :square="imageCropper.square"
      @save="onCropperSave"
      @cancel="onCropperCancel"
    />

  </div>
</template>

<script>
/* eslint-disable */
import PhotoEditor from './PhotoEditor.vue';
import ImageCropper from './ImageCropper.vue';
import RushHourEditor from './rushHourEditor.vue';
import { adminUI } from '../i18n/adminUI.js';
import { DEFAULT_PLANS } from '../plans.js';

const SUPER_ADMINS = ['ephraimichael@gmail.com', 'eifoany@gmail.com'];

export default {
  components: { PhotoEditor, ImageCropper, RushHourEditor },
  data() {
    return {
      authenticated: false,
      emailNotVerified: false,
      authError: false,
      authLoading: true,
      currentUser: null,

      navigationName: '',
      currentLang: 'he',
      availableLangs: [],
      myGames: [],
      activeTab: 'points',
      loading: false,

      allPoints: [],
      onboarding: false,
      newPoint: false,
      wizardStep: 1,
      newNavPoint: false,
      showNpCoords: false,
      showNnpCoords: false,
      showSettingsCoords: false,
      nnp: { pointNumber: '', navigationScreen: 'imagesList', lat: '', lng: '', missingWord: '', navigationOnly: true, _file: null, imagePreview: '' },
      deleteConfirm: { visible: false, pointNumber: null, loading: false },
      np: this.freshPoint(),

      allQuestions: [],
      newQuestion: false,
      nq: this.freshQuestion(),

      settings: {
        displayName: '',
        startLocationName: '',
        mapLat: '', mapLng: '', mapZoom: 15,
        lastPoint: '', pointsOrders: [],
        randomQuestions: false, questionsOrder: 'linkedToPointNumber',
        languages: ['he'],
        bgImageUrl: '', bgImagePreview: '', bgImageFile: null, bgImageSaving: false,
      },
      settingsOriginal: {},

      fieldLink: '',
      fieldLinkLoading: false,

      userPlan: null,
      userPlanConfig: null,

      adminLang: localStorage.getItem('adminLang') || 'fr',

      toast: { show: false, text: '', type: 'success' },
      mapPicker: { open: false, target: null, targetPoint: null, selected: null },
      photoEditor: { open: false, src: '', target: null },
      imageCropper: { show: false, src: '', _resolve: null, square: false },
    };
  },

  computed: {
    isSuperAdmin() {
      return !!(this.currentUser && SUPER_ADMINS.includes(this.currentUser.email));
    },
    gameUrl() {
      return window.location.origin + '/' + this.navigationName;
    },

    gameModeWarnings() {
      if (this.settings.questionsOrder !== 'linkedToPointNumber') return [];
      // Collect all unique point numbers referenced in any order list
      const pointNums = new Set();
      for (const order of this.settings.pointsOrders) {
        const parts = (order.str || '').split(',').map(s => s.trim()).filter(Boolean);
        parts.forEach(n => pointNums.add(n));
      }
      const warnings = [];
      for (const num of pointNums) {
        const point = this.allPoints.find(p => String(p.pointNumber) === String(num));
        const hasContent = point && (
          point.pointInformation || point.audioUrl || point.videoUrl || point.withAR
        );
        const hasQuestions = this.questionsForPoint(num).length > 0;
        if (!hasContent && !hasQuestions) {
          warnings.push(num);
        }
      }
      return warnings;
    },
  },

  mounted() {
    const firebase = require('firebase/app').default;
    firebase.auth().onAuthStateChanged(async (user) => {
      this.authLoading = false;
      if (!user) { this.authenticated = false; return; }
      if (!user.emailVerified) { this.emailNotVerified = true; return; }

      this.authenticated = true;
      this.currentUser = user;

      // Load plan for subscription users (super admins are exempt)
      if (!SUPER_ADMINS.includes(user.email)) {
        const db = firebase.database();
        const planSnap = await db.ref('users/' + user.uid + '/plan').once('value');
        const plan = planSnap.val();
        if (plan) {
          this.userPlan = plan;
          const cfgSnap = await db.ref('/plans/' + plan).once('value');
          this.userPlanConfig = cfgSnap.val() || DEFAULT_PLANS[plan] || DEFAULT_PLANS.free;
        }
      }

      // Load this user's game list for the selection screen
      this.myGames = await this.$store.dispatch('fetchUserGames', user.uid);

      // If ?game= param is in URL, go directly to editor
      const gameParam = this.$route?.query?.game;
      if (gameParam) {
        await this.startEditing(gameParam);
      }
    });
  },

  watch: {
    adminLang(val) { localStorage.setItem('adminLang', val); },
  },

  methods: {

    hasFeature(key) {
      if (this.isSuperAdmin) return true;
      const cfg = this.userPlanConfig || DEFAULT_PLANS.free;
      return !!(cfg.features && cfg.features[key]);
    },

    scrollTop() { window.scrollTo({ top: 0, behavior: 'instant' }); },

    scrollPointIntoView(pointNumber) {
      this.$nextTick(() => {
        const el = document.getElementById('point-card-' + pointNumber);
        if (el) el.scrollIntoView({ block: 'center', behavior: 'instant' });
      });
    },

    screenName(val) {
      const map = {
        imagesList: this.t('screenImagesList'),
        dynamicMap: this.t('screenDynamicMap'),
        carousel: this.t('screenCarousel'),
        missingWord: this.t('screenMissingWord'),
      };
      return map[val] || val;
    },

    pickedName(val) {
      const map = {
        questions: this.t('exerciseQuestions'),
        puzzle: this.t('exercisePuzzle'),
        multiplematch: this.t('exerciseMultipleMatch'),
        memory: this.t('exerciseMemory'),
        justefaux: this.t('exerciseJusteFaux'),
        motManquant: this.t('exerciseMotManquant'),
        rushhour: this.t('exerciseRushHour'),
        signcard: this.t('exerciseSignCard'),
      };
      return map[val] || val;
    },

    t(key) {
      const lang = this.adminLang;
      return (adminUI[lang] && adminUI[lang][key]) || (adminUI['fr'] && adminUI['fr'][key]) || key;
    },

    goToDashboard() {
      this.$router.push('/dashboard');
    },

    async signInWithGoogle() {
      this.authLoading = true;
      this.authError = false;
      const firebase = require('firebase/app').default;
      const provider = new firebase.auth.GoogleAuthProvider();
      try {
        await firebase.auth().signInWithPopup(provider);
      } catch {
        this.authError = true;
        this.authLoading = false;
      }
    },

    async logout() {
      const firebase = require('firebase/app').default;
      await firebase.auth().signOut();
      this.authenticated = false;
      this.navigationName = '';
      this.goToDashboard();
    },

    async startEditing(name) {
      this.navigationName = name;
      this.activeTab = 'points';
      const firebase = require('firebase/app').default;
      const snap = await firebase.database().ref(name + '/languages').once('value');
      this.availableLangs = snap.val() || ['he'];
      this.currentLang = this.availableLangs[0] || 'he';
      await Promise.all([this.loadPoints(), this.loadQuestions()]);
      const onboardKey = 'efoani_onboarded_' + name;
      if (!localStorage.getItem(onboardKey)) {
        if (this.allPoints.length < 4) {
          this.onboarding = true;
          this.openNewPointForm();
        } else {
          // Jeu déjà complet, marquer comme onboardé sans lancer le wizard
          localStorage.setItem(onboardKey, '1');
        }
      }
    },

    async switchLang(lang) {
      this.currentLang = lang;
      await Promise.all([this.loadPoints(), this.loadQuestions()]);
    },

    // ─── POINTS ────────────────────────────────────────────────
    async loadPoints() {
      this.loading = true;
      await this.$store.dispatch('fetchPoints', { name: this.navigationName, lang: this.currentLang });
      const pts = this.$store.getters.points || [];
      const markers = this.$store.getters.markers || [];
      const defaults = this.freshPoint();
      this.allPoints = pts.map(p => {
        const marker = markers[p.pointNumber] || {};
        const merged = {
          ...defaults, ...p,
          lat: marker.lat !== undefined ? marker.lat : (p.lat || ''),
          lng: marker.lng !== undefined ? marker.lng : (p.lng || ''),
        };
        return { ...merged, _open: false, editMode: false, _original: JSON.stringify(merged), _addingQuestion: false, _nq: this.freshQuestion() };
      }).sort((a, b) => Number(a.pointNumber) - Number(b.pointNumber));
      this.loading = false;
    },

    togglePoint(point) {
      if (!point.editMode) point._open = !point._open;
    },

    askDeletePoint(point) {
      this.deleteConfirm = { visible: true, pointNumber: point.pointNumber, loading: false };
    },

    async confirmDeletePoint() {
      this.deleteConfirm.loading = true;
      try {
        await this.$store.dispatch('deletePoint', {
          navigationName: this.navigationName,
          pointNumber: this.deleteConfirm.pointNumber,
        });
        this.deleteConfirm.visible = false;
        await this.loadPoints();
        this.showSuccessToast(this.t('toastPointDeleted'));
      } catch (e) {
        console.error('deletePoint error', e);
        this.deleteConfirm.loading = false;
      }
    },

    openNewPointForm() {
      if (!this.onboarding && this.userPlan && this.userPlanConfig && this.allPoints.length >= this.userPlanConfig.maxPoints) return;
      const nextNum = this.allPoints.length > 0
        ? Math.max(...this.allPoints.map(p => Number(p.pointNumber))) + 1
        : 1;
      this.np = this.freshPoint();
      this.np.pointNumber = nextNum;
      this.wizardStep = 1;
      this.showNpCoords = false;
      this.newPoint = true;
      this.scrollTop();
    },

    async addSubscriptionPoint() {
      if (!this.userPlanConfig) return;
      if (this.allPoints.length >= this.userPlanConfig.maxPoints) return;

      const firebase = require('firebase/app').default;
      const db = firebase.database();
      const game = this.navigationName;
      const nextNum = this.allPoints.length > 0
        ? Math.max(...this.allPoints.map(p => p.pointNumber)) + 1
        : 1;

      // Write empty point to all language branches
      const langs = this.settings.languages.length ? this.settings.languages : ['he'];
      await Promise.all(langs.map(lang =>
        db.ref(game + '/' + lang + '/points/' + nextNum).set({ pointNumber: nextNum })
      ));

      // Update pointsOrder: add nextNum to the end of order1
      const ordersSnap = await db.ref(game + '/pointsOrder').once('value');
      const orders = ordersSnap.val() || { order1: [] };
      const order1 = Array.isArray(orders.order1)
        ? [...orders.order1, nextNum]
        : [...Object.values(orders.order1), nextNum];
      await db.ref(game + '/pointsOrder/order1').set(order1);

      // Update lastPoint to the new last point number
      await db.ref(game + '/lastPoint').set(nextNum);
      this.settings.lastPoint = String(nextNum);

      await this.loadPoints();
      this.showSuccessToast(this.t('pointAdded'));
    },

    cancelEdit(point) {
      const pointNumber = point.pointNumber;
      Object.assign(point, JSON.parse(point._original));
      point.editMode = false;
      point._open = false;
      this.scrollPointIntoView(pointNumber);
    },

    pointDirty(point) {
      if (!point._original) return true;
      const orig = JSON.parse(point._original);
      for (const k of Object.keys(orig)) {
        if (JSON.stringify(point[k]) !== JSON.stringify(orig[k])) return true;
      }
      return false;
    },

    settingsDirty(...fields) {
      if (!this.settingsOriginal || !Object.keys(this.settingsOriginal).length) return false;
      return fields.some(f => JSON.stringify(this.settings[f]) !== JSON.stringify(this.settingsOriginal[f]));
    },

    snapshotSettings(...fields) {
      fields.forEach(f => { this.settingsOriginal[f] = JSON.parse(JSON.stringify(this.settings[f])); });
    },

    addWaypoint(point) {
      if (!point.waypoints) point.waypoints = [];
      point.waypoints.push({ lat: '', lng: '' });
    },

    normalizeJFItems(items) {
      if (!items) return [];
      return Array.isArray(items) ? items : Object.values(items);
    },

    removeJusteFauxItem(target, idx) {
      const arr = this.normalizeJFItems(target.justeFauxItems);
      arr.splice(idx, 1);
      target.justeFauxItems = [...arr];
      this.$forceUpdate();
    },

    addJusteFauxItem(target) {
      // Firebase may return arrays as numeric-keyed objects — normalize before push
      const existing = target.justeFauxItems;
      let arr;
      if (!existing) {
        arr = [];
      } else if (Array.isArray(existing)) {
        arr = [...existing];
      } else {
        arr = Object.values(existing);
      }
      arr.push({ label: '', correct: true });
      target.justeFauxItems = arr;
      this.$forceUpdate();
    },

    async savePoint(point) {
      this.loading = true;
      const oldNumber = JSON.parse(point._original).pointNumber;
      const newNumber = point.pointNumber;

      if (String(oldNumber) !== String(newNumber)) {
        // Renumber: copy all DB entries to new key, delete old ones, update questions
        await this.$store.dispatch('renumberPoint', {
          navigationName: this.navigationName,
          oldNumber,
          newNumber,
        });
      }

      const result = await this.$store.dispatch('updatePoint', { ...point, navigationName: this.navigationName, lang: this.currentLang });
      await this.$store.dispatch('createNewMarker', {
        navigationName: this.navigationName,
        pointNumber: newNumber,
        position: { lat: point.lat, lng: point.lng },
      });
      // Refresh localStorage cache so the game picks up the saved changes immediately
      await this.$store.dispatch('fetchPoints', { name: this.navigationName, lang: this.currentLang });
      if (result && result.imageUrl) point.imageUrl = result.imageUrl;
      point._file = null;
      point.imagePreview = '';
      point.editMode = false;
      point._original = JSON.stringify(point);
      this.loading = false;
      this.scrollPointIntoView(newNumber);
      this.showSuccessToast(this.t('toastPointSaved'));
    },

    addQuestionToNewPoint() {
      if (!this.np._nq.question || !this.np._nq.correctAnswer) {
        this.showErrorToast(this.t('questionRequired'));
        return;
      }
      this.np._questions.push({ ...this.np._nq });
      this.np._nq = this.freshQuestion();
      this.np._addingQuestion = false;
    },

    async submitNewPoint() {
      if (!this.np.pointNumber || !this.np.lat || !this.np.lng) {
        this.showErrorToast(this.t('requiredFields'));
        return;
      }
      if (this.allPoints.some(p => p.pointNumber == this.np.pointNumber)) {
        this.showErrorToast(this.t('pointExists'));
        return;
      }
      this.loading = true;
      await this.$store.dispatch('createNewPoint', { ...this.np, navigationName: this.navigationName, lang: this.currentLang, image: this.np._file || null });
      await this.$store.dispatch('createNewMarker', {
        navigationName: this.navigationName,
        pointNumber: this.np.pointNumber,
        position: { lat: this.np.lat, lng: this.np.lng },
      });
      for (const q of this.np._questions) {
        await this.$store.dispatch('createNewQuestion', {
          navigationName: this.navigationName,
          lang: this.currentLang,
          question: { ...q, pointLinked: this.np.pointNumber },
        });
      }
      this.newPoint = false;
      this.wizardStep = 1;
      this.resetNewPoint();
      this.loading = false;
      await this.loadPoints();
      this.scrollTop();
      this.showSuccessToast(this.t('toastPointCreated'));
      if (this.onboarding) {
        if (this.allPoints.length < 4) {
          this.$nextTick(() => this.openNewPointForm());
        } else {
          this.onboarding = false;
          localStorage.setItem('efoani_onboarded_' + this.navigationName, '1');
        }
      }
    },

    resetNewPoint() { this.np = this.freshPoint(); },

    async onNavPointImagePicked(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.nnp._file = file;
      this.nnp.imagePreview = URL.createObjectURL(file);
      // Extract GPS from EXIF
      const gps = await this.extractGpsFromExif(file).catch(() => null);
      if (gps) { this.nnp.lat = gps.lat; this.nnp.lng = gps.lng; }
    },

    async submitNewNavPoint() {
      if (!this.nnp.pointNumber || !this.nnp.lat || !this.nnp.lng) {
        this.showErrorToast(this.t('requiredFields'));
        return;
      }
      if (!this.nnp._file && !this.nnp.imagePreview) {
        this.showErrorToast(this.t('imageRequired'));
        return;
      }
      if (this.allPoints.some(p => p.pointNumber == this.nnp.pointNumber)) {
        this.showErrorToast(this.t('pointExists'));
        return;
      }
      this.loading = true;
      await this.$store.dispatch('createNewPoint', {
        ...this.nnp,
        navigationName: this.navigationName,
        lang: this.currentLang,
        image: this.nnp._file || null,
        picked: '',
        pointInformation: '',
        audioUrl: '',
        videoUrl: '',
        withVideo: false,
        navigationOnly: true,
      });
      await this.$store.dispatch('createNewMarker', {
        navigationName: this.navigationName,
        pointNumber: this.nnp.pointNumber,
        position: { lat: this.nnp.lat, lng: this.nnp.lng },
      });
      this.newNavPoint = false;
      this.nnp = this.freshNavPoint();
      await this.loadPoints();
      this.showSuccessToast(this.t('toastNavPointCreated'));
    },

    openPhotoEditor(point, mmKey) {
      const src = mmKey ? point[mmKey] : (point.imagePreview || point.imageUrl);
      if (!src) return;
      this.photoEditor = { open: true, src, target: point, mmKey: mmKey || null };
    },

    onPhotoEditorSave(dataUrl) {
      const { target, mmKey } = this.photoEditor;
      if (mmKey) {
        target[mmKey] = dataUrl;
      } else {
        target.imagePreview = dataUrl;
        target.imageBase64  = dataUrl;
        fetch(dataUrl).then(r => r.blob()).then(blob => { target._file = blob; });
      }
      this.photoEditor.open = false;
    },

    processMemoryImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const SIZE = 120;
            const canvas = document.createElement('canvas');
            canvas.width = SIZE;
            canvas.height = SIZE;
            const ctx = canvas.getContext('2d');
            // Cover-fit: crop to square
            const side = Math.min(img.width, img.height);
            const sx = (img.width - side) / 2;
            const sy = (img.height - side) / 2;
            ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    },

    async onMemoryImagePicked(event, target) {
      const file = event.target.files[0];
      if (!file) return;
      if (!target.memoryImages) this.$set(target, 'memoryImages', []);
      if (target.memoryImages.length >= 6) return;
      const dataUrl = await this.processMemoryImage(file);
      target.memoryImages.push(dataUrl);
      this.$forceUpdate();
      event.target.value = '';
    },

    parseVideoInput(val, point) {
      const shortsMatch = val.match(/shorts\/([a-zA-Z0-9_-]{11})/);
      const normalMatch = val.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (shortsMatch) {
        point.videoUrl = shortsMatch[1];
        point.withVerticalVideo = true;
      } else if (normalMatch) {
        point.videoUrl = normalMatch[1];
        point.withVerticalVideo = false;
      } else {
        point.videoUrl = val.trim();
        point.withVerticalVideo = false;
      }
      this.$forceUpdate();
    },

    freshPoint() {
      return {
        pointNumber: '', lat: '', lng: '',
        navigationScreen: 'imagesList', picked: 'questions',
        missingWord: '', pointInformation: '', audioUrl: '',
        withVideo: false, withVerticalVideo: false, withAudio: false, withText: false, withAR: false,
        videoUrl: '', videoBrightness: 100, image: '', imageBase64: '', imageUrl: '',
        noGoogleRoute: false, photoInstruction: '', pictures: {}, timePerPoint: 2,
        imagePreview: '', puzzleImageUrl: '', puzzleImagePreview: '', puzzleImageBase64: '',
        itemImage1: '', itemImage2: '', itemImage3: '', itemImage4: '',
        description1: '', description2: '', description3: '', description4: '',
        waypoints: [], _file: null, _puzzleFile: null,
        arDirection: 180, arDirectionExact: false, arVideoUrl: '', arGifCount: 1, arGifSpacing: 0, arSearchWord: '',
        memoryImages: [],
        signCardImageBase64: '', signCardImageUrl: '', signCardImagePreview: '',
        signCardSigHeightPct: 20, signCardSigVAlign: 'bottom', signCardSigHAlign: 'center',
        _questions: [], _addingQuestion: false, _nq: { question: '', correctAnswer: '', wrongAnswer1: '', wrongAnswer2: '', wrongAnswer3: '' },
      };
    },

    /* ── Image cropper ── */
    openCropper(img, square = false) {
      const tmp = document.createElement('canvas');
      tmp.width = img.width; tmp.height = img.height;
      tmp.getContext('2d').drawImage(img, 0, 0);
      const dataUrl = tmp.toDataURL('image/jpeg', 0.95);
      return new Promise((resolve) => {
        this.imageCropper = { show: true, src: dataUrl, _resolve: resolve, square };
      });
    },

    openCropperFromDataUrl(dataUrl, square = false) {
      return new Promise((resolve) => {
        this.imageCropper = { show: true, src: dataUrl, _resolve: resolve, square };
      });
    },

    onCropperSave(croppedDataUrl) {
      this.imageCropper.show = false;
      const croppedImg = new Image();
      croppedImg.onload = () => this.imageCropper._resolve(croppedImg);
      croppedImg.src = croppedDataUrl;
    },

    onCropperCancel() {
      this.imageCropper.show = false;
      this.imageCropper._resolve(null);
    },

    onNewPointImagePicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      const exifReader = new FileReader();
      exifReader.onload = (e) => {
        const gps = this.extractGpsFromBuffer(e.target.result);
        if (gps) { this.np.lat = gps.lat; this.np.lng = gps.lng; }
      };
      exifReader.readAsArrayBuffer(file);
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = async () => {
        URL.revokeObjectURL(url);
        const finalImg = await this.openCropper(img);
        if (!finalImg) return;
        const maxW = 450;
        const w = Math.min(finalImg.width, maxW);
        const h = Math.round(finalImg.height * (w / finalImg.width));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(finalImg, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        this.np.imagePreview = dataUrl;
        this.np.imageBase64 = dataUrl;
        canvas.toBlob((blob) => { this.np._file = blob; }, 'image/jpeg', 0.85);
      };
      img.src = url;
    },

    onImagePicked(event, point) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const gps = this.extractGpsFromBuffer(e.target.result);
        if (gps) { point.lat = gps.lat; point.lng = gps.lng; }
      };
      reader.readAsArrayBuffer(file);
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = async () => {
        URL.revokeObjectURL(url);
        const finalImg = await this.openCropper(img);
        if (!finalImg) return;
        const maxW = 450;
        const w = Math.min(finalImg.width, maxW);
        const h = Math.round(finalImg.height * (w / finalImg.width));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(finalImg, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        point.imagePreview = dataUrl;
        point.imageBase64 = dataUrl;
        canvas.toBlob((blob) => { point._file = blob; }, 'image/jpeg', 0.85);
      };
      img.src = url;
    },


    arDirectionLabel(deg) {
      const dirs = { 0: 'Nord', 45: 'Nord-Est', 90: 'Est', 135: 'Sud-Est', 180: 'Sud', 225: 'Sud-Ouest', 270: 'Ouest', 315: 'Nord-Ouest' };
      return dirs[deg] !== undefined ? dirs[deg] + ' (' + deg + '°)' : (deg !== undefined ? deg + '°' : '—');
    },

    async onMmImagePicked(event, point, n) {
      const file = event.target.files[0];
      if (!file) return;
      point['itemImage' + n] = await this.processMmImage(file);
    },

    async onNewPointMmImagePicked(event, n) {
      const file = event.target.files[0];
      if (!file) return;
      this.np['itemImage' + n] = await this.processMmImage(file);
    },

    processMmImage(file) {
      return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const targetW = 150, targetH = 200, ratio = targetW / targetH;
          let sx, sy, sw, sh;
          if (img.width / img.height > ratio) { sh = img.height; sw = img.height * ratio; sy = 0; sx = (img.width - sw) / 2; }
          else { sw = img.width; sh = img.width / ratio; sx = 0; sy = (img.height - sh) / 2; }
          const canvas = document.createElement('canvas');
          canvas.width = targetW; canvas.height = targetH;
          canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        };
        img.src = url;
      });
    },

    async _cropPuzzleFromFile(file) {
      const dataUrl = await new Promise(res => {
        const r = new FileReader();
        r.onload = e => res(e.target.result);
        r.readAsDataURL(file);
      });
      const croppedImg = await this.openCropperFromDataUrl(dataUrl, true);
      if (!croppedImg) return null;
      const canvas = document.createElement('canvas');
      canvas.width = 400; canvas.height = 400;
      canvas.getContext('2d').drawImage(croppedImg, 0, 0, 400, 400);
      return canvas.toDataURL('image/jpeg', 0.75);
    },

    async onNewPointPuzzleImagePicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      event.target.value = '';
      const result = await this._cropPuzzleFromFile(file);
      if (!result) return;
      this.np.puzzleImagePreview = result;
      this.np.puzzleImageBase64 = result;
    },

    async onPuzzleImagePicked(event, point) {
      const file = event.target.files[0];
      if (!file) return;
      event.target.value = '';
      const result = await this._cropPuzzleFromFile(file);
      if (!result) return;
      point.puzzleImageUrl = result;
      point.puzzleImageBase64 = result;
      this.$forceUpdate();
    },

    async onSignCardImagePicked(event, point) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processSignCardImage(file);
      point.signCardImageUrl = dataUrl;
      point.signCardImageBase64 = dataUrl;
    },

    async onNewSignCardImagePicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      const dataUrl = await this.processSignCardImage(file);
      this.np.signCardImagePreview = dataUrl;
      this.np.signCardImageBase64 = dataUrl;
    },

    processSignCardImage(file) {
      return new Promise(resolve => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const maxW = 800;
          const scale = img.width > maxW ? maxW / img.width : 1;
          const canvas = document.createElement('canvas');
          canvas.width  = Math.round(img.width  * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.src = url;
      });
    },

    signCardZoneStyle(data) {
      const pct = data.signCardSigHeightPct || 20;
      const vAlign = data.signCardSigVAlign || 'bottom';
      const hAlign = data.signCardSigHAlign || 'center';
      const transforms = [];
      const style = {
        position: 'absolute', height: pct + '%', width: '72%',
        border: '2px dashed rgba(255,255,255,0.75)', borderRadius: '4px',
        background: 'rgba(255,255,255,0.12)', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '700',
        pointerEvents: 'none',
      };
      if (vAlign === 'top')    style.top    = '4%';
      else if (vAlign === 'center') { style.top = '50%'; transforms.push('translateY(-50%)'); }
      else                    style.bottom = '4%';
      if (hAlign === 'left')   style.left   = '4%';
      else if (hAlign === 'center') { style.left = '50%'; transforms.push('translateX(-50%)'); }
      else                    style.right  = '4%';
      if (transforms.length) style.transform = transforms.join(' ');
      return style;
    },

    processPuzzleImage(file) {
      return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const size = Math.min(img.width, img.height);
          const sx = (img.width - size) / 2, sy = (img.height - size) / 2;
          const canvas = document.createElement('canvas');
          canvas.width = 400; canvas.height = 400;
          canvas.getContext('2d').drawImage(img, sx, sy, size, size, 0, 0, 400, 400);
          resolve(canvas.toDataURL('image/jpeg', 0.75));
        };
        img.src = url;
      });
    },

    extractGpsFromBuffer(buffer) {
      try {
        const view = new DataView(buffer);
        if (view.byteLength < 4 || view.getUint16(0) !== 0xFFD8) return null;
        let offset = 2, tiffStart = -1;
        while (offset + 4 < view.byteLength) {
          const marker = view.getUint16(offset);
          const segLen = view.getUint16(offset + 2);
          if (marker === 0xFFDA) break;
          if (marker === 0xFFE1 && view.getUint8(offset + 4) === 0x45 && view.getUint8(offset + 5) === 0x78 && view.getUint8(offset + 6) === 0x69 && view.getUint8(offset + 7) === 0x66 && view.getUint8(offset + 8) === 0x00 && view.getUint8(offset + 9) === 0x00) { tiffStart = offset + 10; break; }
          offset += 2 + segLen;
        }
        if (tiffStart < 0) return null;
        const bo = view.getUint16(tiffStart), le = bo === 0x4949;
        const r16 = (o) => view.getUint16(tiffStart + o, le);
        const r32 = (o) => view.getUint32(tiffStart + o, le);
        const r8 = (o) => view.getUint8(tiffStart + o);
        if (r16(2) !== 42) return null;
        const ifd0 = r32(4), n0 = r16(ifd0);
        let gpsOff = null;
        for (let i = 0; i < n0; i++) { const e = ifd0 + 2 + i * 12; if (r16(e) === 0x8825) { gpsOff = r32(e + 8); break; } }
        if (gpsOff === null) return null;
        const nGps = r16(gpsOff);
        let latRef = 'N', lngRef = 'E', latRaw = null, lngRaw = null;
        for (let i = 0; i < nGps; i++) {
          const e = gpsOff + 2 + i * 12, tag = r16(e), valOff = r32(e + 8);
          if (tag === 0x0001) { latRef = String.fromCharCode(r8(e + 8)); }
          else if (tag === 0x0003) { lngRef = String.fromCharCode(r8(e + 8)); }
          else if (tag === 0x0002) { latRaw = [r32(valOff) / r32(valOff + 4), r32(valOff + 8) / r32(valOff + 12), r32(valOff + 16) / r32(valOff + 20)]; }
          else if (tag === 0x0004) { lngRaw = [r32(valOff) / r32(valOff + 4), r32(valOff + 8) / r32(valOff + 12), r32(valOff + 16) / r32(valOff + 20)]; }
        }
        if (!latRaw || !lngRaw) return null;
        const toDD = (dms, ref) => { const dd = dms[0] + dms[1] / 60 + dms[2] / 3600; return (ref === 'S' || ref === 'W') ? -dd : dd; };
        return { lat: toDD(latRaw, latRef).toFixed(6), lng: toDD(lngRaw, lngRef).toFixed(6) };
      } catch { return null; }
    },

    // ─── QUESTIONS ─────────────────────────────────────────────
    async loadQuestions() {
      await this.$store.dispatch('fetchQuestionsOnly', { name: this.navigationName, lang: this.currentLang });
      const qs = this.$store.getters.questionsList || [];
      this.allQuestions = qs.map(q => ({ ...q, _open: false, editMode: false }));
    },

    async saveQuestion(q) {
      await this.$store.dispatch('updatQuestion', { navigationName: this.navigationName, lang: this.currentLang, key: q.key, question: q.data });
      q.editMode = false;
      this.showSuccessToast(this.t('toastQuestionSaved'));
    },

    questionsForPoint(pointNumber) {
      return this.allQuestions.filter(q => String(q.data.pointLinked) === String(pointNumber));
    },

    async saveGameMode() {
      await this.$store.dispatch('saveGameMode', {
        navigationName: this.navigationName,
        questionsOrder: this.settings.questionsOrder,
        randomQuestions: this.settings.randomQuestions,
      });
      this.snapshotSettings('questionsOrder', 'randomQuestions');
      this.showSuccessToast(this.t('toastMapSaved'));
    },

    async submitQuestionForPoint(point) {
      const nq = point._nq;
      if (!nq.question || !nq.correctAnswer) { this.showErrorToast(this.t('questionRequired')); return; }
      await this.$store.dispatch('createNewQuestion', { navigationName: this.navigationName, lang: this.currentLang, question: { ...nq, pointLinked: point.pointNumber } });
      point._addingQuestion = false;
      point._nq = this.freshQuestion();
      await this.loadQuestions();
      this.showSuccessToast(this.t('toastQuestionCreated'));
    },

    freshQuestion() {
      return { question: '', correctAnswer: '', wrongAnswer1: '', wrongAnswer2: '', wrongAnswer3: '', pointLinked: '' };
    },

    freshNavPoint() {
      return { pointNumber: '', navigationScreen: 'imagesList', lat: '', lng: '', missingWord: '', navigationOnly: true, _file: null, imagePreview: '' };
    },

    // ─── SETTINGS ──────────────────────────────────────────────
    async loadSettings() {
      this.loading = true;
      const firebase = require('firebase/app').default;
      const snap = await firebase.database().ref(this.navigationName).once('value');
      this.loading = false;
      const d = snap.val() || {};
      const mp = d.mapParameters || {};
      this.settings.mapLat = mp.lat || '';
      this.settings.mapLng = mp.lng || '';
      this.settings.mapZoom = mp.zoom || 15;
      this.settings.displayName = d.displayName || '';
      this.settings.startLocationName = d.startLocationName || '';
      this.settings.lastPoint = d.lastPoint || '';
      this.settings.randomQuestions = !!d.randomQuestions;
      this.settings.questionsOrder = d.questionsOrder || 'linkedToPointNumber';
      this.settings.languages = d.languages || ['he'];
      this.settings.bgImageUrl = d.bgImage || '';
      const orders = d.pointsOrder;
      if (orders && typeof orders === 'object') {
        this.settings.pointsOrders = Object.entries(orders).map(([key, arr]) => ({ key, str: (Array.isArray(arr) ? arr : Object.values(arr)).join(', ') }));
      } else { this.settings.pointsOrders = []; }
      this.settingsOriginal = JSON.parse(JSON.stringify(this.settings));
    },

    saveDisplayName() {
      this.$store.dispatch('saveDisplayName', {
        navigationName: this.navigationName,
        displayName: this.settings.displayName.trim(),
      });
      this.snapshotSettings('displayName');
      this.showSuccessToast(this.t('toastDisplayNameSaved'));
    },

    saveStartLocationName() {
      this.$store.dispatch('saveStartLocationName', {
        navigationName: this.navigationName,
        startLocationName: this.settings.startLocationName.trim(),
      });
      this.snapshotSettings('startLocationName');
      this.showSuccessToast(this.t('toastStartLocationSaved'));
    },

    saveMapParameters() {
      this.$store.dispatch('storeMapParameters', { navigationName: this.navigationName, lat: this.settings.mapLat, lng: this.settings.mapLng, zoom: this.settings.mapZoom });
      this.snapshotSettings('mapLat', 'mapLng', 'mapZoom');
      this.showSuccessToast(this.t('toastMapSaved'));
    },

    saveLastPoint() {
      this.$store.dispatch('storeEndPoint', { navigationName: this.navigationName, endPoint: this.settings.lastPoint });
      this.snapshotSettings('lastPoint');
      this.showSuccessToast(this.t('toastLastPointSaved'));
    },

    async saveAllPointsOrders() {
      for (const order of this.settings.pointsOrders) {
        const arr = order.str.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
        if (!arr.length) continue;
        if (order.key) { await this.$store.dispatch('updatePointsOrder', { navigationName: this.navigationName, key: order.key, order: arr }); }
        else { await this.$store.dispatch('storePointsOrder', { navigationName: this.navigationName, pointsOrder: arr }); }
      }
      this.snapshotSettings('pointsOrders');
      this.showSuccessToast(this.t('toastOrdersSaved'));
    },

    async saveLanguages() {
      const langs = this.settings.languages.length ? this.settings.languages : ['he'];
      this.settings.languages = langs;
      const firebase = require('firebase/app').default;
      const db = firebase.database();
      await db.ref(this.navigationName + '/languages').set(langs);
      // Ensure language data structures exist for new languages
      await Promise.all(langs.map(async lang => {
        const snap = await db.ref(this.navigationName + '/' + lang).once('value');
        if (!snap.exists()) {
          await db.ref(this.navigationName + '/' + lang).set({ points: [], questions: [] });
        }
      }));
      this.snapshotSettings('languages');
      this.showSuccessToast(this.t('toastLangsSaved'));
    },

    async deleteOrder(order, index) {
      if (order.key) { await this.$store.dispatch('deletePointsOrder', { navigationName: this.navigationName, key: order.key }); }
      this.settings.pointsOrders.splice(index, 1);
      this.showSuccessToast(this.t('toastOrderDeleted'));
    },

    onBgImagePicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.settings.bgImageFile = file;
      this.settings.bgImagePreview = URL.createObjectURL(file);
    },

    async saveBgImage() {
      if (!this.settings.bgImageFile) return;
      this.settings.bgImageSaving = true;
      try {
        const url = await this.$store.dispatch('saveBgImage', {
          navigationName: this.navigationName,
          file: this.settings.bgImageFile,
        });
        this.settings.bgImageUrl = url;
        this.settings.bgImagePreview = '';
        this.settings.bgImageFile = null;
        this.showSuccessToast(this.t('toastBgImageSaved'));
      } catch (e) {
        this.showErrorToast(this.t('toastBgImageError'));
      } finally {
        this.settings.bgImageSaving = false;
      }
    },

    copyGameUrl() {
      navigator.clipboard.writeText(this.gameUrl).then(() => { this.showSuccessToast(this.t('toastUrlCopied')); });
    },

    async generateFieldLink() {
      this.fieldLinkLoading = true;
      const db = require('firebase/app').default.database();
      const token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      await db.ref(this.navigationName + '/fieldToken').set(token);
      const base = window.location.origin;
      this.fieldLink = base + '/field?game=' + this.navigationName + '&token=' + token + '&lang=' + this.adminLang;
      this.fieldLinkLoading = false;
    },

    copyFieldLink() {
      navigator.clipboard.writeText(this.fieldLink).then(() => { this.showSuccessToast(this.t('fieldLinkCopied')); });
    },

    showSuccessToast(text) { this.toast = { show: true, text, type: 'success' }; setTimeout(() => { this.toast.show = false; }, 2500); },
    showErrorToast(text) { this.toast = { show: true, text, type: 'error' }; setTimeout(() => { this.toast.show = false; }, 3000); },

    openMapPicker(target, point) {
      this.mapPicker = { open: true, target, targetPoint: point || null, selected: null };
      this.$nextTick(() => this.initMapPicker());
    },

    async initMapPicker() {
      const API_KEY = 'AIzaSyACbBFnoaG5EVR7-IDGn8lsiTtPHxWQWB4';
      if (!window.google || !window.google.maps) {
        await new Promise((resolve, reject) => {
          window._mapPickerResolve = resolve;
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&callback=_mapPickerResolve`;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      const defaultLat = parseFloat(this.settings.mapLat) || 32.657;
      const defaultLng = parseFloat(this.settings.mapLng) || 35.417;
      const zoom = this.mapPicker.target === 'settings' ? (parseInt(this.settings.mapZoom) || 15) : 15;
      const map = new window.google.maps.Map(document.getElementById('map-picker-container'), {
        center: { lat: defaultLat, lng: defaultLng },
        zoom,
      });

      // Afficher tous les points existants
      const lastPointNum = parseInt(this.settings.lastPoint);
      this.allPoints.forEach((p) => {
        if (!p.lat || !p.lng) return;
        const isLast = p.pointNumber == lastPointNum;
        new window.google.maps.Marker({
          position: { lat: parseFloat(p.lat), lng: parseFloat(p.lng) },
          map,
          title: `Point ${p.pointNumber}`,
          icon: isLast ? 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                       : 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
          label: { text: String(p.pointNumber), color: 'white', fontSize: '11px', fontWeight: 'bold' },
        });
      });

      let clickMarker = null;
      map.addListener('click', (e) => {
        const lat = e.latLng.lat().toFixed(6);
        const lng = e.latLng.lng().toFixed(6);
        this.mapPicker = { ...this.mapPicker, selected: { lat, lng } };
        if (clickMarker) clickMarker.setMap(null);
        clickMarker = new window.google.maps.Marker({ position: e.latLng, map });
      });
    },

    applyMapCoords() {
      if (!this.mapPicker.selected) return;
      const { lat, lng } = this.mapPicker.selected;
      if (this.mapPicker.target === 'np') {
        this.np.lat = lat;
        this.np.lng = lng;
      } else if (this.mapPicker.target === 'nnp') {
        this.nnp.lat = lat;
        this.nnp.lng = lng;
      } else if (this.mapPicker.target === 'settings') {
        this.settings.mapLat = lat;
        this.settings.mapLng = lng;
      } else if (this.mapPicker.targetPoint) {
        this.mapPicker.targetPoint.lat = lat;
        this.mapPicker.targetPoint.lng = lng;
      }
      this.mapPicker.open = false;
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.auth-screen { display: flex; align-items: center; justify-content: center; min-height: 100vh; background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%); }
.auth-card { background: white; border-radius: 16px; padding: 40px 32px; width: 100%; max-width: 380px; box-shadow: 0 20px 60px rgba(0,0,0,0.25); text-align: center; }
.auth-logo { font-size: 48px; margin-bottom: 12px; }
.auth-card h2 { color: #1a1a2e; margin: 0 0 8px; font-size: 24px; }
.auth-card .text-muted { color: #666; font-size: 14px; margin-bottom: 24px; }
.account-row { display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 16px; }
.account-email { color: #888; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.btn-account-logout { background: none; border: none; color: #1a73e8; font-size: 13px; font-weight: 600; cursor: pointer; flex-shrink: 0; }
.btn-account-logout:hover { text-decoration: underline; }
.error-msg { color: #e53935; font-size: 13px; margin: 8px 0; }
.game-list { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; max-height: 300px; overflow-y: auto; }
.game-item { padding: 12px 16px; background: #e8f5e9; border: 1px solid #a5d6a7; border-radius: 8px; cursor: pointer; font-size: 15px; font-weight: 500; color: #1b5e20; text-align: left; }
.game-item:hover { background: #c8e6c9; }
.topbar { background: #1a73e8; color: white; padding: 8px 16px; display: flex; flex-direction: column; gap: 6px; position: sticky; top: 0; z-index: 50; }
.topbar-row { display: flex; align-items: center; width: 100%; }
.topbar-row--title { justify-content: space-between; align-items: center; }
.topbar-row--tabs { gap: 8px; }
.topbar-row--actions { gap: 8px; flex-wrap: wrap; }
.topbar-back { padding: 6px 12px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.4); background: transparent; color: white; font-size: 12px; cursor: pointer; white-space: nowrap; margin-inline-start: auto; margin-inline-end: 0; }
.topbar-back:hover { background: rgba(255,255,255,0.15); }
.topbar-game { display: flex; flex-direction: column; gap: 1px; }
.topbar-game-label { font-size: 10px; font-weight: 500; opacity: 0.7; text-transform: uppercase; letter-spacing: 0.5px; }
.topbar-game-name { font-weight: 700; font-size: 16px; white-space: nowrap; }
.tab-btn { flex: 1; padding: 10px 14px; border-radius: 8px; border: none; background: rgba(255,255,255,0.15); color: white; font-size: 15px; font-weight: 600; cursor: pointer; text-align: center; }
.tab-btn:hover { background: rgba(255,255,255,0.25); }
.tab-btn.active { background: white; color: #1a73e8; font-weight: 700; }
.topbar-logout { padding: 6px 12px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.4); background: transparent; color: white; font-size: 12px; cursor: pointer; }
.lang-switcher { display: flex; gap: 4px; }
.lang-btn { padding: 4px 10px; border-radius: 12px; border: 1.5px solid rgba(255,255,255,0.7); background: transparent; color: white; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.15s; }
.lang-btn.active { background: white; color: #1a73e8; border-color: white; }
.lang-btn:hover:not(.active) { background: rgba(255,255,255,0.2); }
.wiz-steps-row { display: flex; align-items: center; gap: 4px; }
.wiz-dot { width: 24px; height: 24px; border-radius: 50%; background: #e2e8f0; color: #94a3b8; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.wiz-dot-active { background: #1a73e8; color: white; }
.wiz-dot-line { width: 20px; height: 2px; background: #e2e8f0; }
.wiz-step-label { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
.wiz-step-hint { font-size: 13px; color: #64748b; margin-bottom: 16px; }
.wiz-options-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 8px; }
.wiz-opt { border: 2px solid #e2e8f0; border-radius: 10px; padding: 12px; cursor: pointer; transition: border-color 0.15s, background 0.15s; background: #fff; }
.wiz-opt:hover { border-color: #93c5fd; background: #f0f7ff; }
.wiz-opt--selected { border-color: #1a73e8; background: #eff6ff; }
.wiz-opt-icon { font-size: 22px; margin-bottom: 4px; }
.wiz-opt-name { font-size: 13px; font-weight: 700; color: #1e293b; margin-bottom: 3px; }
.wiz-opt-desc { font-size: 11px; color: #64748b; line-height: 1.4; }
.wiz-field-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.btn-map-pick { padding: 7px 12px; background: #34a853; color: white; border: none; border-radius: 6px; font-size: 13px; cursor: pointer; white-space: nowrap; }
.btn-map-pick:hover { background: #2d8e47; }
.coord-row { display: flex; align-items: center; gap: 8px; }
.btn-coords-toggle { padding: 7px 10px; background: #f1f5f9; color: #64748b; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px; cursor: pointer; line-height: 1; }
.btn-coords-toggle:hover { background: #e2e8f0; color: #334155; }
.btn-coords-toggle.active { background: #e0f2fe; color: #0369a1; border-color: #7dd3fc; }
.info-checkboxes { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 6px; }
.map-picker-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1000; display: flex; align-items: center; justify-content: center; }
.map-picker-modal { background: white; border-radius: 12px; overflow: hidden; width: 90%; max-width: 700px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
.map-picker-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: #1a73e8; color: white; font-weight: 600; }
.map-picker-close { background: none; border: none; color: white; font-size: 18px; cursor: pointer; line-height: 1; }
.map-picker-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-top: 1px solid #eee; }
.map-picker-coords { font-size: 14px; color: #333; font-family: monospace; }
.map-picker-hint { font-size: 13px; color: #999; }
.tab-content { padding: 16px; max-width: 720px; margin: 0 auto; }
.card { background: white; border-radius: 12px; margin-bottom: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); overflow: hidden; }
.card--editing { position: fixed; top: 0; bottom: 0; left: 50%; transform: translateX(-50%); width: 100%; max-width: 720px; z-index: 10001; margin: 0; border-radius: 0; overflow-y: auto; background: #f0f4f8; }
.card--editing .card-header { position: sticky; top: 0; z-index: 1; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.card-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; cursor: pointer; user-select: none; transition: background 0.15s; }
.card-header:hover { background: #f5f5f5; }
.card-title { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; }
.point-meta { display: flex; flex-direction: column; gap: 3px; }
.point-meta-row { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
.card-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.card-body { padding: 16px; }
.add-card { border: 2px dashed #b0bec5; background: #fafafa; text-align: center; padding: 20px; color: #546e7a; font-weight: 600; cursor: pointer; border-radius: 12px; }
.add-card:hover { border-color: #1a73e8; color: #1a73e8; background: #e8f0fe; }
.new-form-card { border: 2px solid #1a73e8; }
.new-form-card .card-header { background: #e8f0fe; }
.onboarding-alert { background: #fffbeb; border-left: 4px solid #f59e0b; color: #92400e; font-weight: 600; font-size: 14px; padding: 10px 16px; margin: 0; }
.onboarding-counter { font-size: 11px; color: #1a73e8; font-weight: 700; letter-spacing: 0.5px; margin-top: 2px; }
.chevron { font-size: 12px; color: #9e9e9e; }
.point-badge { background: #1a73e8; color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.point-label { font-size: 10px; color: #9e9e9e; font-weight: 500; white-space: nowrap; }
.point-phase-label { font-size: 14px; flex-shrink: 0; }
.point-screen-badge { background: #e8f0fe; color: #1565c0; border-radius: 4px; padding: 2px 7px; font-size: 11px; font-weight: 600; }
.point-picked-badge { background: #fce4ec; color: #880e4f; border-radius: 4px; padding: 2px 7px; font-size: 11px; font-weight: 600; }
.point-info-badge { border-radius: 4px; padding: 2px 6px; font-size: 10px; font-weight: 600; }
.point-info-badge.text { background: #e8f5e9; color: #2e7d32; }
.point-info-badge.video { background: #ede7f6; color: #512da8; }
.point-info-badge.audio { background: #fff3e0; color: #e65100; }
.point-info-badge.ar { background: #e8eaf6; color: #3949ab; }
.point-info-badge.phone { background: #e0f7fa; color: #00695c; }
.point-info-badge.empty { color: #bdbdbd; }
.point-missing-word { font-size: 11px; font-weight: 600; color: #333; background: #fff9c4; border-radius: 4px; padding: 2px 7px; }
.point-q-badge { background: #f5f5f5; color: #616161; border-radius: 4px; padding: 2px 7px; font-size: 11px; font-weight: 600; margin-left: 4px; }
.point-questions-section { border-top: 1px solid #e8eaf6; background: #f8f9ff; padding: 12px 16px; }
.pqs-header { font-size: 12px; font-weight: 700; color: #3949ab; margin-bottom: 8px; }
.pq-card { background: white; border: 1px solid #e0e0e0; border-radius: 8px; margin-bottom: 6px; overflow: hidden; }
.pq-card-header { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; cursor: pointer; gap: 8px; }
.pq-card-header:hover { background: #f5f5f5; }
.pq-card-header--read { cursor: default; }
.pq-card-header--read:hover { background: transparent; }
.pq-text { font-size: 13px; color: #333; flex: 1; }
.pq-body { padding: 8px 12px; }
.pq-new { padding: 12px; border: 1px dashed #3949ab; background: #f3f4ff; }
.pqs-add-btn { width: 100%; padding: 7px; border: 1px dashed #3949ab; border-radius: 6px; background: transparent; color: #3949ab; font-size: 12px; cursor: pointer; margin-top: 4px; }
.pqs-add-btn:hover { background: #e8eaf6; }
.read-section { margin-bottom: 0; padding: 14px 0; border-bottom: 1px solid #e8eaf0; }
.read-section:last-child { border-bottom: none; }
.read-section-title { font-size: 11px; font-weight: 700; color: #9e9e9e; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px; display: inline-flex; align-items: center; gap: 5px; }

/* ── 3-phase read view ── */
.card-body.read-phases { padding: 0; }
.read-phases > .read-phase { padding: 14px 16px; border-bottom: 2px solid #f0f2f5; }
.read-phases > .read-phase:last-child { border-bottom: none; }
.read-phase-title { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.7px; margin-bottom: 10px; display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 10px; }
.read-phases > .read-phase:nth-child(1) .read-phase-title { background: #e8f0fe; color: #1565c0; }
.read-phases > .read-phase:nth-child(2) .read-phase-title { background: #e8f5e9; color: #2e7d32; }
.read-phases > .read-phase:nth-child(3) .read-phase-title { background: #ede7f6; color: #4527a0; }

/* ── phase header in edit mode ── */
.phase-header { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: #1565c0; background: #e8f0fe; border-left: 3px solid #1a73e8; padding: 8px 16px 8px 13px; margin: 18px -16px 14px; display: flex; align-items: center; justify-content: space-between; }
/* Section card layout */
.sections-wrapper { display: flex; flex-direction: column; gap: 10px; padding: 10px; background: #f5f7fb; }
.section-card { background: white; border-radius: 10px; border: 1px solid #e8eaf0; padding: 14px 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.section-card--compact { padding: 10px 16px; }
.section-card--standalone { margin: 0 10px 10px; }
.section-card--questions { margin: 0 10px 0; }
.sc-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.sc-title { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.7px; display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 10px; }
.sc-title--screen  { background: #e8f0fe; color: #1565c0; }
.sc-title--info    { background: #e8f5e9; color: #2e7d32; }
.sc-title--exercise{ background: #ede7f6; color: #4527a0; }
.sc-title--position{ background: #e0f7fa; color: #00695c; }
.sc-header .help-tip-text { left: auto; right: 0; transform: none; }
.position-desc { font-size: 13px; color: #555; margin: 0 0 10px; }
.help-tip { display: inline-flex; align-items: center; justify-content: center; width: 15px; height: 15px; border-radius: 50%; background: white; color: inherit; border: 1.5px solid currentColor; font-size: 9px; font-weight: 700; cursor: help; position: relative; flex-shrink: 0; text-transform: none; letter-spacing: 0; opacity: 0.85; }
.help-tip-text { display: none; position: absolute; bottom: calc(100% + 6px); left: 50%; right: auto; transform: translateX(-50%); background: #333; color: #fff; font-size: 11px; font-weight: 400; text-transform: none; letter-spacing: 0; padding: 7px 10px; border-radius: 6px; width: 210px; white-space: normal; z-index: 200; pointer-events: none; line-height: 1.45; box-shadow: 0 2px 8px rgba(0,0,0,0.25); }
.phase-header .help-tip-text { left: auto; right: 0; transform: none; }
.help-tip:hover .help-tip-text { display: block; }
.read-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.read-item { display: flex; flex-direction: column; gap: 2px; }
.read-item .label { font-size: 11px; color: #9e9e9e; text-transform: uppercase; }
.read-field { display: flex; flex-direction: column; gap: 2px; margin-bottom: 6px; }
.read-field > .label { font-size: 11px; color: #9e9e9e; text-transform: uppercase; }
.read-value { font-size: 13px; color: #333; word-break: break-all; }
.muted { color: #bdbdbd; font-size: 13px; }
.info-text { font-size: 13px; color: #555; white-space: pre-wrap; background: #f9f9f9; padding: 10px; border-radius: 6px; margin: 4px 0 0; }
.point-thumb { max-width: 100%; max-height: 200px; border-radius: 8px; object-fit: cover; display: block; }
.section-hr {
  border: none;
  border-top: 2px solid #e8f0fe;
  margin: 16px 0;
}
.file-btn {
  display: inline-block;
  padding: 7px 14px;
  background: #e8f0fe;
  color: #1a73e8;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid #c5d8fc;
  transition: background 0.15s;
}
.file-btn:hover { background: #d2e3fc; }
.puzzle-thumb { width: 160px; height: 160px; border-radius: 8px; object-fit: cover; display: block; border: 2px solid #b0bec5; }

/* ── Juste/Faux admin ─────────────────────────────────── */
.jf-admin-row { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
.jf-label-input { flex: 1; }
.jf-toggle-btn { min-width: 36px; height: 36px; border: 2px solid #b0bec5; border-radius: 8px; background: white; font-size: 16px; cursor: pointer; font-weight: bold; color: #607d8b; transition: background 0.15s, color 0.15s, border-color 0.15s; }
.jf-active-vrai { background: #e8f5e9; border-color: #4caf50; color: #2e7d32; }
.jf-active-faux { background: #ffebee; border-color: #f44336; color: #c62828; }
.jf-remove-btn { background: none; border: none; cursor: pointer; font-size: 16px; opacity: 0.6; padding: 4px; }
.jf-remove-btn:hover { opacity: 1; }

/* ── Mots Manquants admin ─────────────────────────────── */
.mm-mode-toggle { display: flex; gap: 8px; margin-top: 6px; }
.mm-mode-btn { flex: 1; padding: 8px 12px; border: 2px solid #b0bec5; border-radius: 10px; background: white; font-size: 13px; font-weight: 600; cursor: pointer; color: #546e7a; transition: background 0.15s, border-color 0.15s, color 0.15s; }
.mm-mode-active { background: #e3f2fd; border-color: #1976d2; color: #1565c0; }
.ar-compile-progress { margin-top: 8px; font-size: 13px; color: #e65100; background: #fff3e0; padding: 6px 10px; border-radius: 6px; }
.ar-compile-ok { margin-top: 8px; font-size: 13px; color: #2e7d32; background: #e8f5e9; padding: 6px 10px; border-radius: 6px; }
.edit-form { background: #fafafa; }
.field-row { display: flex; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.field-group { flex: 1; min-width: 140px; display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
.field-label { font-size: 12px; font-weight: 600; color: #555; text-transform: uppercase; letter-spacing: 0.03em; }
.form-input-sm, .form-textarea { width: 100%; padding: 8px 12px; border: 1.5px solid #ddd; border-radius: 8px; font-size: 14px; outline: none; transition: border 0.15s; background: white; }
.form-textarea { resize: vertical; }
.form-input-sm:focus, .form-textarea:focus { border-color: #1a73e8; }
.input-error { border-color: #e53e3e !important; background: #fff5f5 !important; }
.input-error:focus { border-color: #c53030 !important; }
.correct-input { border-color: #a5d6a7; background: #f1f8e9; }
.correct-input:focus { border-color: #388e3c; }
.section-divider { border-top: 1px solid #e0e0e0; margin: 16px 0 12px; font-size: 11px; font-weight: 700; color: #9e9e9e; text-transform: uppercase; padding-top: 12px; }
.answer-list { display: flex; flex-direction: column; gap: 6px; }
.answer { padding: 8px 12px; border-radius: 6px; font-size: 13px; }
.answer.correct { background: #e8f5e9; color: #2e7d32; font-weight: 600; }
.answer.wrong { background: #fce4ec; color: #880e4f; }
.gps-badge { margin-top: 8px; background: #e8f5e9; color: #2e7d32; border-radius: 6px; padding: 6px 10px; font-size: 12px; font-weight: 600; }
.thumb-row { display: flex; align-items: flex-start; gap: 10px; flex-wrap: wrap; }
.btn-edit-photo { margin-top: 8px; padding: 6px 14px; border-radius: 8px; border: 1.5px solid #3b82f6; background: #eff6ff; color: #1d4ed8; font-size: 13px; font-weight: 600; cursor: pointer; }
.btn-edit-photo:hover { background: #dbeafe; }
.ar-gif-options { display: flex; gap: 12px; margin-top: 8px; }
.ar-dir-toggle { display: flex; gap: 6px; }
.ar-anim-toggle { display: flex; gap: 6px; margin-top: 4px; }
.ar-dir-btn { flex: 1; padding: 6px 10px; border-radius: 8px; border: 1.5px solid #cbd5e1; background: #f8fafc; color: #475569; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.ar-dir-btn.active { background: #2563eb; border-color: #2563eb; color: #fff; }
.ar-deg-input { display: flex; align-items: center; gap: 6px; }
.ar-deg-unit { font-size: 16px; font-weight: 700; color: #475569; }
.field-hint { font-size: 11px; color: #94a3b8; margin-top: 3px; }
.mode-cards { display: flex; gap: 12px; margin-top: 8px; }
.mode-card { flex: 1; border: 2px solid #e2e8f0; border-radius: 12px; padding: 14px; cursor: pointer; transition: border-color 0.15s, background 0.15s; background: #f8fafc; }
.mode-card:hover { border-color: #93c5fd; background: #eff6ff; }
.mode-card.active { border-color: #3b82f6; background: #eff6ff; }
.mode-card-icon { font-size: 24px; margin-bottom: 6px; }
.mode-card-title { font-weight: 700; font-size: 14px; color: #1e40af; margin-bottom: 4px; }
.mode-card.active .mode-card-title { color: #1d4ed8; }
.mode-card-desc { font-size: 12px; color: #64748b; line-height: 1.4; }
.settings-section { background: white; border-radius: 12px; padding: 20px; margin-bottom: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.settings-title { font-weight: 700; font-size: 16px; color: #333; margin-bottom: 16px; }
.settings-required { color: #e53e3e; font-size: 18px; vertical-align: middle; }
.settings-coords-badge { background: #e8f5e9; color: #2e7d32; border-radius: 8px; padding: 8px 12px; font-size: 13px; font-weight: 600; display: inline-block; }
.settings-coords-missing { background: #fff8e1; color: #e65100; border-radius: 8px; padding: 8px 12px; font-size: 13px; font-weight: 600; display: inline-block; }
.si-card { border: 1px solid #e2e8f0; border-radius: 10px; margin-bottom: 10px; overflow: hidden; }
.si-card-header { display: flex; align-items: center; gap: 10px; padding: 12px 14px; background: #f8fafc; cursor: pointer; user-select: none; }
.si-card-header:hover { background: #eff6ff; }
.si-card-num { min-width: 28px; height: 28px; border-radius: 50%; background: #1a73e8; color: #fff; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.si-card-summary { flex: 1; font-size: 13px; color: #444; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.si-card-body { padding: 14px; border-top: 1px solid #e2e8f0; }
.si-card-actions { display: flex; gap: 8px; margin-top: 14px; }
.si-q-row { background: #f8fafc; border-radius: 8px; padding: 10px; margin-bottom: 8px; border: 1px solid #e2e8f0; }
.btn-danger-sm { background: #fee2e2; color: #c0392b; border: 1px solid #fca5a5; border-radius: 6px; padding: 6px 12px; font-size: 12px; cursor: pointer; font-weight: 600; }
.checkbox-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.check-label { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #444; cursor: pointer; }
.check-label input[type=checkbox] { width: 16px; height: 16px; cursor: pointer; }
.checkbox-row { display: flex; gap: 20px; flex-wrap: wrap; padding: 4px 0; }
.order-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.order-index { font-size: 12px; font-weight: 600; color: #666; white-space: nowrap; min-width: 60px; }
.help-text { font-size: 13px; color: #666; margin-bottom: 12px; }
.url-display { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; background: #f0f4f8; border-radius: 8px; padding: 12px 16px; }
.url-text { font-size: 14px; color: #1a73e8; font-weight: 600; word-break: break-all; flex: 1; }
.btn-copy-url { padding: 6px 12px; background: #e8f0fe; color: #1565c0; border: 1px solid #90caf9; border-radius: 6px; font-size: 13px; cursor: pointer; white-space: nowrap; }
.btn-copy-url:hover { background: #bbdefb; }
.btn-google { width: 100%; padding: 13px; background: white; color: #3c4043; border: 1px solid #dadce0; border-radius: 10px; font-size: 15px; font-weight: 500; cursor: pointer; margin-top: 12px; display: flex; align-items: center; justify-content: center; }
.btn-google:hover { box-shadow: 0 1px 6px rgba(0,0,0,0.2); }
.btn-google:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-success { padding: 9px 18px; background: #2e7d32; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; }
.btn-success:hover { background: #1b5e20; }
.btn-success:disabled { background: #9e9e9e; cursor: not-allowed; }
.btn-secondary { padding: 9px 18px; background: #e0e0e0; color: #333; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; }
.btn-secondary:hover { background: #bdbdbd; }
.btn-edit { padding: 4px 10px; background: #e3f2fd; color: #1565c0; border: 1px solid #90caf9; border-radius: 6px; font-size: 12px; cursor: pointer; }
.btn-save { padding: 4px 10px; background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; border-radius: 6px; font-size: 12px; cursor: pointer; }
.btn-save:disabled { background: #f5f5f5; color: #bdbdbd; border-color: #e0e0e0; cursor: not-allowed; }
.btn-cancel { padding: 4px 10px; background: #fce4ec; color: #880e4f; border: 1px solid #f48fb1; border-radius: 6px; font-size: 12px; cursor: pointer; }
.btn-ghost { padding: 6px 12px; background: transparent; color: #1a73e8; border: 1.5px dashed #90caf9; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; display: block; }
.btn-ghost:hover { background: #e8f0fe; }
.btn-danger-sm { padding: 4px 10px; background: #ffebee; color: #c62828; border: 1px solid #ef9a9a; border-radius: 6px; font-size: 12px; cursor: pointer; }
.row-buttons { display: flex; gap: 10px; justify-content: flex-end; }
.upload-hint { font-size: 12px; color: #888; margin-bottom: 4px; }
.mem-img-preview-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.mem-img-preview { position: relative; width: 60px; height: 60px; }
.mem-img-preview img { width: 60px; height: 60px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; display: block; }
.mem-img-remove { position: absolute; top: -6px; right: -6px; width: 18px; height: 18px; border-radius: 50%; border: none; background: #e53935; color: #fff; font-size: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
.field-warn { font-size: 12px; color: #e65100; margin-top: 4px; }
.mm-read-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-top: 8px; }
.mm-pair-view { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.mm-thumb { width: 150px; height: 200px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; display: block; }
.mm-empty { width: 150px; height: 200px; background: #f0f0f0; border-radius: 6px; border: 1px dashed #ccc; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #aaa; }
.mm-desc { font-size: 13px; font-weight: 600; text-align: center; color: #333; }
.mm-pair-edit { margin-bottom: 12px; padding: 12px; background: #f0f4f8; border-radius: 8px; border: 1px solid #dde3ef; }
.mm-pair-header { font-size: 12px; font-weight: 700; color: #1a73e8; text-transform: uppercase; margin-bottom: 8px; }
.mm-pair-row { display: flex; gap: 16px; align-items: flex-start; }
.mm-img-col { display: flex; flex-direction: column; gap: 4px; flex-shrink: 0; }
.mm-label-col { flex: 1; display: flex; flex-direction: column; gap: 4px; justify-content: center; }
.spinner-wrap { display: flex; justify-content: center; padding: 40px; }
.spinner { width: 36px; height: 36px; border: 3px solid #e0e0e0; border-top-color: #1a73e8; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.toast-msg { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 12px 24px; border-radius: 10px; font-weight: 600; font-size: 14px; z-index: 9999; box-shadow: 0 4px 20px rgba(0,0,0,0.2); }
.toast-msg.success { background: #2e7d32; color: white; }
.toast-msg.error { background: #c62828; color: white; }
.mt-1 { margin-top: 6px; } .mt-2 { margin-top: 8px; } .mt-3 { margin-top: 12px; } .mt-4 { margin-top: 16px; }
.mb-2 { margin-bottom: 8px; }

/* ── Add cards row ── */
.add-cards-row { display: flex; gap: 12px; }
.add-cards-row .add-card { flex: 1; }
.add-card--nav { border-color: #a5d6a7 !important; color: #2e7d32 !important; background: #f1f8f1 !important; }
.add-card--nav:hover { background: #e8f5e9 !important; }
.add-card--disabled { border-color: #e2e8f0 !important; color: #94a3b8 !important; background: #f8fafc !important; cursor: not-allowed; }
.add-card--limit { border-color: #fbbf24 !important; color: #92400e !important; background: #fffbeb !important; cursor: pointer; }
.add-card--limit:hover { background: #fef3c7 !important; border-color: #f59e0b !important; }
.add-card--limit-link { color: #1a73e8; font-weight: 700; text-decoration: underline; }
.plan-points-bar { padding: 8px 0 12px; width: 100%; }
.plan-points-count { font-size: 13px; color: #64748b; font-weight: 600; display: block; margin-bottom: 6px; }
.plan-points-track { height: 6px; background: #e2e8f0; border-radius: 3px; overflow: hidden; }
.plan-points-fill { height: 100%; background: #1a73e8; border-radius: 3px; transition: width 0.3s ease; }

/* ── Nav-only badge ── */
.point-navonly-badge { display: inline-block; padding: 2px 8px; background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7; border-radius: 10px; font-size: 11px; font-weight: 600; }

/* ── Delete point button ── */
.btn-delete-point { padding: 4px 8px; background: #ffebee; color: #c62828; border: 1px solid #ef9a9a; border-radius: 6px; font-size: 13px; cursor: pointer; }
.btn-delete-point:hover { background: #ffcdd2; }

/* ── Delete confirmation dialog ── */
.delete-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 24px;
}
.delete-dialog {
  background: #fff; border-radius: 20px; padding: 32px 28px;
  max-width: 360px; width: 100%; text-align: center;
  box-shadow: 0 12px 40px rgba(0,0,0,0.25);
}
.delete-dialog-icon { font-size: 48px; margin-bottom: 12px; }
.delete-dialog-title { font-size: 18px; font-weight: 700; color: #1a1a2e; margin-bottom: 10px; }
.delete-dialog-sub { font-size: 14px; color: #666; line-height: 1.5; margin-bottom: 24px; }
.delete-dialog-actions { display: flex; gap: 12px; justify-content: center; }
.delete-btn-cancel { padding: 10px 24px; background: #f0f0f0; color: #333; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; }
.delete-btn-cancel:hover { background: #e0e0e0; }
.delete-btn-confirm { padding: 10px 24px; background: #c62828; color: #fff; border: none; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; }
.delete-btn-confirm:hover:not(:disabled) { background: #b71c1c; }
.delete-btn-confirm:disabled { opacity: 0.6; cursor: not-allowed; }

.mode-warning-box {
  background: #fff8e1;
  border: 1px solid #f9a825;
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 12px;
  color: #5d3a00;
  font-size: 13px;
  line-height: 1.6;
}
.mode-warning-badge {
  display: inline-block;
  background: #f57f17;
  color: #fff;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 8px;
  margin: 2px 3px;
  font-size: 13px;
}

.brightness-slider { width: 100%; accent-color: #f59e0b; }

/* ── Sign Card admin ───────────────────────────────────── */
.sc-admin-preview-wrap { position: relative; display: inline-block; border-radius: 8px; overflow: hidden; max-width: 100%; }
.sc-admin-img { display: block; max-width: 100%; max-height: 260px; border-radius: 8px; }
.sc-range { width: 100%; accent-color: #2563eb; }
.sc-align-row { display: flex; gap: 6px; }
.sc-align-btn { flex: 1; padding: 6px 8px; border-radius: 7px; border: 1.5px solid #cbd5e1; background: #f8fafc; color: #475569; font-size: 12px; font-weight: 700; cursor: pointer; }
.sc-align-btn.sc-align-active { background: #2563eb; border-color: #2563eb; color: #fff; }
.admin-lang-switcher { display: flex; gap: 4px; align-items: center; }
.admin-lang-btn { background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.3); border-radius: 6px; padding: 4px 8px; font-size: 16px; cursor: pointer; transition: background 0.15s; }
.admin-lang-btn.admin-lang-active { background: rgba(255,255,255,0.4); border-color: white; }
</style>
